/**
 * Centralized RBAC Actions
 * These actions will gradually replace the database-driven _rbac_action table.
 *
 * This file is in the shared library to be accessible by both server and web applications.
 *
 * The nested structure is used for better organization in the UI when assigning actions.
 */

/**
 * Interface for a single RBAC Action definition
 */
export interface RbacActionDefinition {
	label: string;
	action: string;
	description: string;
}

/**
 * Interface for a group of RBAC Actions (recursive structure)
 * Note: We use a type union to handle the label property alongside the index signature
 */
export type RbacActionGroup = {
	label: string;
} & {
	[key: string]: RbacActionDefinition | RbacActionGroup | string;
};

/**
 * Interface for the top-level RBAC Actions model
 */
export interface RbacActionsModel {
	[key: string]: RbacActionGroup | RbacActionDefinition;
}

/**
 * Helper function to check if an item is an RbacAction
 */
export function isRbacAction(item: unknown): item is RbacActionDefinition {
	return (
		item != null &&
		typeof item === 'object' &&
		'action' in item &&
		'description' in item &&
		'label' in item
	);
}

/**
 * Helper function to check if an item is an RbacActionGroup
 */
export function isRbacActionGroup(item: unknown): item is RbacActionGroup {
	return (
		item != null &&
		typeof item === 'object' &&
		'label' in item &&
		!('action' in item)
	);
}

/**
 * RBAC Action definitions with nested/recursive structure for UI organization
 */
export const RbacActions = {
	ACCESS_LOGS: {
		label: 'Access Logs',
		READ: {
			label: 'View Access Logs',
			action: 'access_logs:read',
			description: 'Ability to view access logs for instance',
		},
		LOGIN_HISTORY: {
			label: 'Login History',
			READ: {
				label: 'View Login History',
				action: 'access_logs:login_history:read',
				description: 'Ability to view login history for instance',
			},
			// PD-14200 — grants the Login History page restricted to ecommerce
			// (portal) logins only. Callers holding this WITHOUT the full READ
			// above have the User Type filter locked to Ecommerce and never see
			// CRM login history (enforced server-side by the crm-api proxy).
			ECOMMERCE_READ: {
				label: 'View Ecommerce Login History',
				action: 'access_logs:login_history:ecommerce:read',
				description:
					'Ability to view ecommerce (portal) login history only, without access to CRM login history',
			},
		},
	},
	AI_AGENTS: {
		label: 'AI Agents',
		AGENTS: {
			label: 'Agents',
			READ: {
				label: 'View Agents',
				action: 'ai_agents:agents:read',
				description: 'Ability to view AI agents and their details',
			},
			CREATE: {
				label: 'Create Agents',
				action: 'ai_agents:agents:create',
				description: 'Ability to create new AI agents',
			},
			UPDATE: {
				label: 'Edit Agents',
				action: 'ai_agents:agents:update',
				description: 'Ability to edit existing AI agents',
			},
			DELETE: {
				label: 'Delete Agents',
				action: 'ai_agents:agents:delete',
				description: 'Ability to delete AI agents',
			},
		},
		TOOLS: {
			label: 'Tools',
			READ: {
				label: 'View Tools',
				action: 'ai_agents:tools:read',
				description: 'Ability to view AI agent tools',
			},
			CREATE: {
				label: 'Create Tools',
				action: 'ai_agents:tools:create',
				description: 'Ability to create new AI agent tools',
			},
			UPDATE: {
				label: 'Edit Tools',
				action: 'ai_agents:tools:update',
				description: 'Ability to edit existing AI agent tools',
			},
			DELETE: {
				label: 'Delete Tools',
				action: 'ai_agents:tools:delete',
				description: 'Ability to delete AI agent tools',
			},
		},
		VOICES: {
			label: 'Voices',
			READ: {
				label: 'View Voices',
				action: 'ai_agents:voices:read',
				description: 'Ability to view voice profiles',
			},
			CREATE: {
				label: 'Clone Voices',
				action: 'ai_agents:voices:create',
				description: 'Ability to create custom cloned voices',
			},
			UPDATE: {
				label: 'Edit Voices',
				action: 'ai_agents:voices:update',
				description: 'Ability to edit voice profiles and favorites',
			},
			DELETE: {
				label: 'Delete Voices',
				action: 'ai_agents:voices:delete',
				description: 'Ability to delete custom cloned voices',
			},
		},
		SESSIONS: {
			label: 'Conversations',
			READ: {
				label: 'View Conversations',
				action: 'ai_agents:sessions:read',
				description: 'Ability to view AI agent conversation sessions',
			},
		},
		ANALYZERS: {
			label: 'Performance Analyzers',
			READ: {
				label: 'View Analyzers',
				action: 'ai_agents:analyzers:read',
				description: 'Ability to view performance analyzers',
			},
			CREATE: {
				label: 'Create Analyzers',
				action: 'ai_agents:analyzers:create',
				description: 'Ability to create performance analyzers',
			},
			UPDATE: {
				label: 'Edit Analyzers',
				action: 'ai_agents:analyzers:update',
				description: 'Ability to edit performance analyzers',
			},
			DELETE: {
				label: 'Delete Analyzers',
				action: 'ai_agents:analyzers:delete',
				description: 'Ability to delete performance analyzers',
			},
		},
		PERFORMANCE: {
			label: 'Performance Reports',
			READ: {
				label: 'View Performance',
				action: 'ai_agents:performance:read',
				description: 'Ability to view performance history and reports',
			},
		},
		MONITORING: {
			label: 'Monitoring',
			READ: {
				label: 'View Monitoring',
				action: 'ai_agents:monitoring:read',
				description: 'Ability to view real-time monitoring dashboard',
			},
			MANAGE_CAPACITY: {
				label: 'Manage Capacity Limits',
				action: 'ai_agents:monitoring:manage_capacity',
				description: 'Ability to view and modify capacity limits',
			},
		},
		USAGE: {
			label: 'Usage',
			READ: {
				label: 'View Usage',
				action: 'ai_agents:usage:read',
				description: 'Ability to view usage statistics and history',
			},
		},
		CHAT_WIDGET: {
			label: 'Chat Widget',
			USE: {
				label: 'Use Chat Widget',
				action: 'ai_agents:chat_widget:use',
				description:
					'Ability to chat with the system AI assistant (Sales Helper) from the CRM navigation widget',
			},
			MANAGE: {
				label: 'Manage Chat Widget',
				action: 'ai_agents:chat_widget:manage',
				description:
					'Ability to enable/disable the system AI agent chat widget for the tenant',
			},
		},
		REPORTING_TOOLS: {
			label: 'AI Reporting Tools',
			USE: {
				label: 'Use AI Reporting Tools',
				action: 'ai_agents:reporting_tools:use',
				// Categorical gate for the AI agent's report-building tool group
				// (describe_schema / validate_query / execute_query / save_report /
				// render_view). These tools execute raw SELECT SQL against tenant
				// tables (client, invoice, order, user, …) with NO per-row
				// salesperson scoping — so "can use the group" IS the data-access
				// boundary. Grant only to roles already trusted with full tenant
				// data visibility (admin / manager level), not regular reps.
				description:
					'Ability to have the AI assistant build and run custom data reports. These reports can read across all tenant data without the usual per-rep scoping, so grant only to roles trusted with full data access.',
			},
		},
	},
	AI_ASSISTANT: {
		label: 'AI Assistant',
		READ: {
			label: 'Access AI Assistant',
			action: 'ai_assistant:read',
			description:
				'Ability to see the AI Assistant navigation section and access its pages (Overview, Getting Started, Call Log, Analytics, Agents, Voice Training, Phone Numbers). Gating happens at the nav layer — revoking this action hides the entire AI Assistant menu group and blocks direct URL navigation to /ai-assistant/* routes. Distinct from the AI_AGENTS actions, which gate the older /ai-agents/* configuration surface.',
		},
	},
	CHAT: {
		label: 'Chat',
		READ: {
			label: 'Use Chat',
			action: 'chat:read',
			description:
				'Baseline access to chat. Required to view any conversation, send DMs, or read messages. Users without this action cannot open the chat surface at all.',
		},
		CHANNEL: {
			label: 'Channels',
			CREATE: {
				label: 'Create Channels',
				action: 'chat:channel:create',
				description:
					'Create new channels (private by default; team/role gates optional). Public channels require chat:channel:create_public in addition.',
			},
			CREATE_PUBLIC: {
				label: 'Create Public Channels',
				action: 'chat:channel:create_public',
				description:
					'Create — or flip an existing channel to — tenant-wide public, so any user with chat:read can discover and post into it. Implies chat:channel:create.',
			},
		},
		QUEUE: {
			label: 'Customer Support Queue',
			// View + Claim are NOT separate RBAC actions. They are
			// derived from the per-user `canReceiveCustomerChats` flag —
			// one source of truth, gated by `MANAGE_ASSIGNMENTS` below.
			// A user is either customer-facing (sees the queue, can
			// claim) or not (queue pane hidden, claim API rejects).
			// This avoids the historical "two locks on one door"
			// confusion where an admin had to grant a role action AND
			// flip a per-user flag for a rep to actually receive
			// customer chats.
			MANAGE_ASSIGNMENTS: {
				label: 'Assign Customer Chat Eligibility',
				action: 'chat:queue:manage_assignments',
				description:
					'Set which reps are eligible to receive customer-support chats. This is a workforce-management role assignment — reps cannot self-assign or self-remove. Use status (Available/Away/Offline) for day-to-day availability instead.',
			},
		},
		SUPPORT: {
			label: 'Customer Support History',
			VIEW_ARCHIVED: {
				label: 'View Archived Customer Support Chats',
				action: 'chat:support:view_archived',
				description:
					'View all resolved customer-support conversations across the tenant, including transcripts. Meant for supervisors / auditors — reps only see their own live chats.',
			},
		},
	},
	COMPETITIVE_PRICING: {
		label: 'Competitive Pricing',
		VIEW_SELF: {
			label: 'View My Competitive Prices',
			action: 'competitive_pricing:view:self',
			description: 'View competitive price records that the user created',
		},
		VIEW_MY_CLIENTS: {
			label: 'View Competitive Prices for My Clients',
			action: 'competitive_pricing:view:my_clients',
			description:
				'View all competitive price records for clients assigned to the user',
		},
		VIEW_ANY: {
			label: 'View All Competitive Prices',
			action: 'competitive_pricing:view:any',
			description:
				'View all competitive price records regardless of client assignment',
		},
		CREATE: {
			label: 'Create Competitive Prices',
			action: 'competitive_pricing:create',
			description: 'Create new competitive price records',
		},
		UPDATE_SELF: {
			label: 'Edit My Competitive Prices',
			action: 'competitive_pricing:update:self',
			description: 'Edit competitive price records that the user created',
		},
		UPDATE_ANY: {
			label: 'Edit All Competitive Prices',
			action: 'competitive_pricing:update:any',
			description: 'Edit any competitive price record regardless of creator',
		},
		DELETE_SELF: {
			label: 'Delete My Competitive Prices',
			action: 'competitive_pricing:delete:self',
			description: 'Delete competitive price records that the user created',
		},
		DELETE_ANY: {
			label: 'Delete All Competitive Prices',
			action: 'competitive_pricing:delete:any',
			description: 'Delete any competitive price record regardless of creator',
		},
	},
	CROSS_REFERENCE: {
		label: 'Cross Reference',
		READ: {
			label: 'View Cross-Reference Requests',
			action: 'cross_reference:read',
			description:
				'Ability to view the cross-reference request queue, its lines and the candidate items the matching engine proposed',
		},
		REVIEW: {
			label: 'Review Cross-Reference Matches',
			action: 'cross_reference:review',
			description:
				'Ability to confirm or reject proposed matches, mark a line as no-match and assign the client on an unresolved request. Confirming also teaches the matching engine a durable equivalence.',
		},
		SOURCES_REVIEW: {
			label: 'Review Cross-Reference Lookup Sources',
			action: 'cross_reference:sources:review',
			description:
				'Ability to see and judge WHERE a match came from: the per-source evidence behind an enriched line, excluding or restoring a source, the structured reason captured when rejecting a candidate (and taking that rejection back), and the reviewer-feedback panel on the learning screen. Granted to no role by default — without it the review screen behaves as it did before per-source review existed.',
		},
		MANAGE_LEARNING: {
			label: 'Manage Learned Equivalences',
			action: 'cross_reference:learning:manage',
			description:
				'Ability to retire or reactivate a learned equivalence. Separate from reviewing because a learned row is not the decision on a single line: retiring or reactivating it changes how every future match scores, tenant-wide.',
		},
	},
	CLIENTS: {
		label: 'Clients',
		READ: {
			label: 'View Clients',
			action: 'clients:read',
			description: 'Ability to view client details and lists',
		},
		VIEW_SELF: {
			label: 'View Assigned Clients',
			action: 'clients:view:self',
			description:
				'Can view clients where user is sales rep (enables access to client detail pages, subtabs, and related records)',
		},
		VIEW_UNASSIGNED: {
			label: 'View Unassigned Clients',
			action: 'clients:view:unassigned',
			description:
				'Can view clients that have no sales rep assigned (leads and customers with no assigned salesperson)',
		},
		LIST_ANY: {
			label: 'List All Clients',
			action: 'clients:list:any',
			description:
				'Can see every client in list views regardless of sales rep assignment. Does NOT grant access to detail pages of clients the user does not own.',
		},
		DETAIL_ANY: {
			label: 'View All Client Details',
			action: 'clients:detail:any',
			description:
				'Can open the detail page of any client regardless of sales rep assignment (bypasses ownership restrictions for client detail and related records).',
		},
		LEADS: {
			label: 'Leads',
			CREATE: {
				label: 'Create Leads',
				action: 'clients:leads:create',
				description: 'Ability to create new leads',
			},
			UPDATE: {
				label: 'Edit Leads',
				action: 'clients:leads:update',
				description: 'Ability to edit existing leads',
			},
		},
		CUSTOMERS: {
			label: 'Customers',
			CREATE: {
				label: 'Create Customers',
				action: 'clients:customers:create',
				description: 'Ability to create new customers',
			},
			UPDATE: {
				label: 'Edit Customers',
				action: 'clients:customers:update',
				description: 'Ability to edit existing customers',
			},
		},
		DISTRIBUTION: {
			label: 'Distribution Settings',
			UPDATE: {
				label: 'Edit Distribution Settings',
				action: 'clients:distribution:update',
				description:
					'Ability to edit distribution / delivery settings on a client (full-pallet, auto-generate orders, Saturday delivery, night eligibility, signature requirement, delivery schedule).',
			},
		},
		INSIGHTS: {
			label: 'Client Insights',
			action: 'clients:insights',
			description: 'Ability to access client insights and analytics',
		},
		ITEM_HISTORY: {
			label: 'Client Item History',
			action: 'clients:item_history',
			description: 'Ability to view client item history',
		},
		SHOP: {
			label: 'Client Shop Access',
			action: 'clients:shop_access',
			description: 'Ability to access client shop',
		},
		CONTACTS: {
			label: 'Client Contacts',
			CREATE: {
				label: 'Create Client Contacts',
				action: 'clients:contacts:create',
				description: 'Ability to create new client contacts',
			},
			READ: {
				label: 'View Client Contacts',
				action: 'clients:contacts:read',
				description: 'Ability to view client contact details and lists',
			},
			UPDATE: {
				label: 'Edit Client Contacts',
				action: 'clients:contacts:update',
				description: 'Ability to edit existing client contacts',
			},
			DELETE: {
				label: 'Delete Client Contacts',
				action: 'clients:contacts:delete',
				description: 'Ability to delete client contacts',
			},
		},
		NOTES: {
			label: 'Client Notes',
			CREATE: {
				label: 'Create Client Notes',
				action: 'clients:notes:create',
				description: 'Ability to create notes for clients',
			},
			READ: {
				label: 'View Client Notes',
				action: 'clients:notes:read',
				description: 'Ability to view notes associated with clients',
			},
			UPDATE_SELF: {
				label: 'Update Own Client Notes',
				action: 'clients:notes:update:self',
				description: 'Ability to update notes you created for clients',
			},
			UPDATE_ANY: {
				label: 'Update Any Client Notes',
				action: 'clients:notes:update:any',
				description: 'Ability to update any notes for clients',
			},
			DELETE_SELF: {
				label: 'Delete Own Client Notes',
				action: 'clients:notes:delete:self',
				description: 'Ability to delete notes you created for clients',
			},
		},
		CALLS: {
			label: 'Client Calls',
			CREATE: {
				label: 'Log Client Calls',
				action: 'clients:calls:create',
				description: 'Ability to log calls for clients',
			},
			READ: {
				label: 'View Client Calls',
				action: 'clients:calls:read',
				description: 'Ability to view calls associated with clients',
			},
			UPDATE: {
				label: 'Update Client Calls',
				action: 'clients:calls:update',
				description: 'Ability to update existing client calls',
			},
			DELETE: {
				label: 'Delete Client Calls',
				action: 'clients:calls:delete',
				description: 'Ability to delete existing client calls',
			},
		},
		VISITS: {
			label: 'Client Visits',
			CREATE: {
				label: 'Log Client Visits',
				action: 'clients:visits:create',
				description: 'Ability to log visits for clients',
			},
			READ: {
				label: 'View Client Visits',
				action: 'clients:visits:read',
				description: 'Ability to view visits associated with clients',
			},
			DELETE: {
				label: 'Delete Client Visits',
				action: 'clients:visits:delete',
				description: 'Ability to delete existing client visits',
			},
		},
		DOCUMENTS: {
			label: 'Client Documents',
			CREATE: {
				label: 'Upload Client Documents',
				action: 'clients:documents:create',
				description: 'Ability to upload documents for clients',
			},
			READ: {
				label: 'View Client Documents',
				action: 'clients:documents:read',
				description: 'Ability to view documents associated with clients',
			},
			DELETE: {
				label: 'Delete Client Documents',
				action: 'clients:documents:delete',
				description: 'Ability to delete existing client documents',
			},
			DOWNLOAD: {
				label: 'Download Client Documents',
				action: 'clients:documents:download',
				description: 'Ability to download client documents',
			},
		},
		PAYMENT_METHODS: {
			label: 'Client Payment Methods',
			CREATE: {
				label: 'Add Client Payment Methods',
				action: 'clients:payment_methods:create',
				description: 'Ability to add payment methods for clients',
			},
			READ: {
				label: 'View Client Payment Methods',
				action: 'clients:payment_methods:read',
				description: 'Ability to view payment methods associated with clients',
			},
			UPDATE: {
				label: 'Update Client Payment Methods',
				action: 'clients:payment_methods:update',
				description: 'Ability to update existing client payment methods',
			},
			DELETE: {
				label: 'Delete Client Payment Methods',
				action: 'clients:payment_methods:delete',
				description: 'Ability to delete existing client payment methods',
			},
			MARK_PRIMARY: {
				label: 'Mark Primary Client Payment Method',
				action: 'clients:payment_methods:mark_primary',
				description: 'Ability to mark a payment method as primary for clients',
			},
		},
		LICENSES: {
			label: 'Client Licenses',
			CREATE: {
				label: 'Create Client Licenses',
				action: 'clients:licenses:create',
				description: 'Ability to create new client licenses',
			},
			READ: {
				label: 'View Client Licenses',
				action: 'clients:licenses:read',
				description: 'Ability to view client license details',
			},
			UPDATE: {
				label: 'Update Client Licenses',
				action: 'clients:licenses:update',
				description: 'Ability to update existing client licenses',
			},
			DELETE: {
				label: 'Delete Client Licenses',
				action: 'clients:licenses:delete',
				description: 'Ability to delete client licenses',
			},
		},
	},
	/**
	 * Sales Routes (PD-13776) — a salesperson's recurring plan for visiting
	 * their own accounts. Unlike the delivery ROUTES/TRIPS namespace (the
	 * distribution side), sales routes generate NO trips or stops; they expand
	 * on the fly into a daily "who am I due to see" list and are checked off
	 * via the existing client-visit flow. Access mirrors client ownership:
	 * reps manage their OWN routes (`:self`), leaders manage everyone's
	 * (`:any`). Seeded by *AddSalesRoutesRbac* (parity with clients:view:self /
	 * clients:detail:any).
	 */
	SALES_ROUTES: {
		label: 'Sales Routes',
		VIEW_SELF: {
			label: 'View Own Sales Routes',
			action: 'sales_routes:view:self',
			description:
				'Can view sales routes the user owns (their own recurring client-visit plans) and the daily due-list they produce.',
		},
		VIEW_ANY: {
			label: 'View All Sales Routes',
			action: 'sales_routes:view:any',
			description:
				"Can view every salesperson's sales routes regardless of owner (leadership oversight); routes are labelled by owner.",
		},
		MANAGE_SELF: {
			label: 'Manage Own Sales Routes',
			action: 'sales_routes:manage:self',
			description:
				'Can create, edit, and delete sales routes the user owns, and check off / skip their clients.',
		},
		MANAGE_ANY: {
			label: 'Manage All Sales Routes',
			action: 'sales_routes:manage:any',
			description:
				"Can create, edit, and delete any salesperson's sales routes, bypassing ownership.",
		},
	},
	AUTOMATION: {
		label: 'Automation',
		READ: {
			label: 'View Automations',
			action: 'automation:read',
			description: 'Ability to view automation rules and workflows',
		},
		CREATE: {
			label: 'Create Automations',
			action: 'automation:create',
			description: 'Ability to create new automation rules',
		},
		UPDATE: {
			label: 'Update Automations',
			action: 'automation:update',
			description: 'Ability to update existing automation rules',
		},
		DELETE: {
			label: 'Delete Automations',
			action: 'automation:delete',
			description: 'Ability to delete automation rules',
		},
		EXECUTE: {
			label: 'Execute Automations',
			action: 'automation:execute',
			description: 'Ability to manually trigger automation workflows',
		},
	},
	CALL_HISTORY: {
		label: 'Call History',
		READ: {
			label: 'View Call History',
			action: 'call_history:read',
			description: 'Ability to view call history lists and details',
		},
		VIEW_SELF: {
			label: 'View Own Call History',
			action: 'call_history:view:self',
			description: 'Ability to view own call history entries',
		},
		VIEW_ANY: {
			label: 'View All Call History',
			action: 'call_history:view:any',
			description: 'Ability to view all call history regardless of ownership',
		},
	},
	ANNOUNCEMENTS: {
		label: 'Announcements',
		CREATE: {
			label: 'Create Announcements',
			action: 'announcements:create',
			description: 'Ability to create new announcements',
		},
		READ: {
			label: 'View Announcements',
			action: 'announcements:read',
			description: 'Ability to view announcement details and lists',
		},
		UPDATE: {
			label: 'Edit Announcements',
			action: 'announcements:update',
			description: 'Ability to edit existing announcements',
		},
		DELETE: {
			label: 'Delete Announcements',
			action: 'announcements:delete',
			description: 'Ability to delete announcements',
		},
	},
	EMAIL: {
		label: 'Email',
		SEND: {
			label: 'Send Emails',
			action: 'email:send',
			description: 'Ability to send emails',
		},
		READ: {
			label: 'View Emails',
			action: 'email:read',
			description: 'Ability to view emails',
		},
		MAILBOX: {
			label: 'Connect External Email',
			action: 'email:mailbox:connect',
			description:
				'Ability to connect an external email account (e.g., Gmail, Outlook) for sending and receiving emails',
		},
		SENT_EMAILS: {
			label: 'Sent Emails',
			READ: {
				label: 'View Sent Emails',
				action: 'email:sent:view',
				description: 'Ability to view sent emails',
			},
		},
		TEMPLATES: {
			label: 'Email Templates',
			CREATE: {
				label: 'Create Email Templates',
				action: 'email:templates:create',
				description: 'Ability to create new email templates',
			},
			READ: {
				label: 'View Email Templates',
				action: 'email:templates:view',
				description: 'Ability to view existing email templates',
			},
			UPDATE: {
				label: 'Update Email Templates',
				action: 'email:templates:update',
				description: 'Ability to update existing email templates',
			},
		},
		DOMAINS: {
			label: 'Email Domains',
			READ: {
				label: 'View Email Domains',
				action: 'email:domains:read',
				description:
					'Ability to view email domain settings and DNS verification records',
			},
			UPDATE: {
				label: 'Manage Email Domains',
				action: 'email:domains:update',
				description: 'Ability to add, remove, and verify email sending domains',
			},
		},
	},
	PHONE: {
		label: 'Phone & SMS',
		SMS: {
			label: 'SMS',
			SEND: {
				label: 'Send SMS',
				action: 'phone:sms:send',
				description: 'Ability to send SMS messages',
			},
			READ: {
				label: 'View SMS Status',
				action: 'phone:sms:read',
				description: 'Ability to view SMS message status',
			},
		},
		CALLS: {
			label: 'Phone Calls',
			CREATE: {
				label: 'Make Calls',
				action: 'phone:calls:create',
				description: 'Ability to initiate phone calls',
			},
			READ: {
				label: 'View Call Status',
				action: 'phone:calls:read',
				description: 'Ability to view phone call status',
			},
		},
		VALIDATE: {
			label: 'Validate Phone Numbers',
			action: 'phone:validate',
			description: 'Ability to validate phone numbers',
		},
		SYSTEM_CONFIGURE: {
			label: 'Configure Phone System',
			action: 'phone:system:configure',
			description:
				'Manage tenant phone numbers, destinations (User / Group / Role / AI Agent), per-destination ring strategy + fallback, and tenant-wide phone-system defaults. Gates the Communications admin screen. Granted to Admin and manager-level roles.',
		},
		COACHING: {
			label: 'Live Call Coaching (All Calls)',
			action: 'phone:coaching:any',
			description:
				'Listen to, whisper into, or barge into ANY active call across the tenant from the live-call monitor. Compliance-sensitive: every session lands an audit row. Blanket scope — granted to Admin. Managers who should only coach their own team get the team-scoped action instead.',
		},
		COACHING_TEAM: {
			label: 'Live Call Coaching (My Team)',
			action: 'phone:coaching:team',
			description:
				'Listen to, whisper into, or barge into active calls handled by members of the team the user manages — the subordinate set resolved from the manager hierarchy (managed groups via group parent, plus user-manager and role-manager links), the same scope that drives the live-call monitor. Compliance-sensitive: every session lands an audit row. Granted to Sales Manager / Purchasing Manager / Warehouse Manager / Receiving Manager.',
		},
		E911_MANAGE_SELF: {
			label: 'Manage My Emergency Address',
			action: 'phone:e911:manage:self',
			description:
				'Ability to view and update the calling user’s own E911 registered address (RAY BAUM §506). Granted to every softphone user — the rep is the source of truth for where they are. Does NOT include the ability to edit another user’s address.',
		},
	},
	GROUPS: {
		label: 'Groups',
		CREATE: {
			label: 'Create Groups',
			action: 'groups:create',
			description: 'Ability to create new groups',
		},
		READ: {
			label: 'View Groups',
			action: 'groups:read',
			description: 'Ability to view group details and lists',
		},
		UPDATE: {
			label: 'Edit Groups',
			action: 'groups:update',
			description: 'Ability to edit existing groups',
		},
		DELETE: {
			label: 'Delete Groups',
			action: 'groups:delete',
			description: 'Ability to delete groups',
		},
		MANAGE_CLIENTS: {
			label: 'Manage Group Clients',
			action: 'groups:clients:manage',
			description: 'Ability to add or remove clients from groups',
		},
		MANAGE_ITEMS: {
			label: 'Manage Group Items',
			action: 'groups:items:manage',
			description: 'Ability to add or remove items from groups',
		},
		MANAGE_USERS: {
			label: 'Manage Group Users',
			action: 'groups:users:manage',
			description: 'Ability to add or remove users from groups',
		},
	},
	GOALS: {
		label: 'Goals',
		USER: {
			label: 'User Goals',
			CREATE: {
				label: 'Create User Goals',
				action: 'goals:user:create',
				description: 'Ability to create goals for users',
			},
			READ: {
				label: 'View User Goals',
				action: 'goals:user:view',
				description: 'Ability to view goals assigned to users',
			},
			UPDATE: {
				label: 'Update User Goals',
				action: 'goals:user:update',
				description: 'Ability to update goals assigned to users',
			},
			DELETE: {
				label: 'Delete User Goals',
				action: 'goals:user:delete',
				description: 'Ability to delete goals assigned to users',
			},
		},
		TEAM: {
			label: 'Team Goals',
			CREATE: {
				label: 'Create Team Goals',
				action: 'goals:team:create',
				description: 'Ability to create goals for teams',
			},
			READ: {
				label: 'View Team Goals',
				action: 'goals:team:view',
				description: 'Ability to view goals assigned to teams',
			},
			UPDATE: {
				label: 'Update Team Goals',
				action: 'goals:team:update',
				description: 'Ability to update goals assigned to teams',
			},
			DELETE: {
				label: 'Delete Team Goals',
				action: 'goals:team:delete',
				description: 'Ability to delete goals assigned to teams',
			},
		},
		COMPANY: {
			label: 'Company Goals',
			CREATE: {
				label: 'Create Company Goals',
				action: 'goals:company:create',
				description: 'Ability to create goals for the company',
			},
			READ: {
				label: 'View Company Goals',
				action: 'goals:company:view',
				description: 'Ability to view goals assigned to the company',
			},
			UPDATE: {
				label: 'Update Company Goals',
				action: 'goals:company:update',
				description: 'Ability to update goals assigned to the company',
			},
			DELETE: {
				label: 'Delete Company Goals',
				action: 'goals:company:delete',
				description: 'Ability to delete goals assigned to the company',
			},
		},
	},
	INTEGRATIONS: {
		label: 'Integrations',
		CLIENT_ITEM: {
			label: 'Client Item Integration',
			EXECUTE: {
				label: 'Execute Client Item Integration Job',
				action: 'integrations:client_item:execute',
				description: 'Ability to execute client item integration jobs',
			},
		},
	},
	INVOICES: {
		label: 'Invoices',
		CREATE: {
			label: 'Create Invoices',
			action: 'invoices:create',
			description: 'Ability to create new invoices',
		},
		VIEW_OWN: {
			label: 'View Own Invoices',
			action: 'invoices:view:own',
			description:
				'Can view invoices for clients the user owns (assigned salesperson, alternate, or unassigned when paired with clients:view:unassigned).',
		},
		VIEW_ANY: {
			label: 'View All Invoices',
			action: 'invoices:view:any',
			description:
				'Can view invoices for any client regardless of sales rep assignment (bypasses ownership restrictions).',
		},
		UPDATE: {
			label: 'Edit Invoices',
			action: 'invoices:update',
			description: 'Ability to edit existing invoices',
		},
		DELETE: {
			label: 'Delete Invoices',
			action: 'invoices:delete',
			description: 'Ability to delete invoices',
		},
		EMAIL: {
			label: 'Email Invoices',
			action: 'invoices:email',
			description: 'Ability to email invoices',
		},
		EXPORT: {
			label: 'Export Invoices',
			action: 'invoices:export',
			description: 'Ability to export invoice data',
		},
		CREATE_CREDIT_MEMO: {
			label: 'Issue Credit Memos',
			action: 'invoices:credit_memo:create',
			description:
				'Ability to issue a credit memo against a client, giving money back. Deliberately separate from invoices:create — billing a customer and crediting one are different authorities, and a credit reduces revenue. Only available when Workd is the tenant ERP; when a third-party ERP owns the ledger, credits are issued there.',
		},
	},
	TERMS: {
		label: 'Terms',
		EDIT: {
			label: 'Edit Terms on Records',
			action: 'terms:edit',
			description:
				'Ability to set / change the terms code on a client or invoice, and to override the early-pay discount amount at payment time. Separate from settings:terms_codes:manage (which gates the catalog itself). Typically granted to Admin and Accounting.',
		},
	},
	ISSUES: {
		label: 'Issues',
		CREATE: {
			label: 'Create Issues',
			action: 'issues:create',
			description: 'Ability to create new issues',
		},
		READ: {
			label: 'View Issues',
			action: 'issues:read',
			description: 'Ability to view existing issues',
		},
		VIEW_SELF: {
			label: 'View Own Issues',
			action: 'issues:view:self',
			description:
				'Can view issues where user is owner or client sales rep (enables access to issue detail pages)',
		},
		VIEW_ANY: {
			label: 'View All Issues',
			action: 'issues:view:any',
			description:
				'Can view all issues regardless of ownership (bypasses ownership restrictions)',
		},
		UPDATE: {
			label: 'Update Issues',
			action: 'issues:update',
			description: 'Ability to update existing issues',
		},
		DELETE: {
			label: 'Delete Issues',
			action: 'issues:delete',
			description: 'Ability to delete existing issues',
		},
		NOTES: {
			label: 'Issue Notes',
			CREATE: {
				label: 'Create Issue Notes',
				action: 'issues:notes:create',
				description: 'Ability to create issue notes',
			},
			READ: {
				label: 'View Issue Notes',
				action: 'issues:notes:read',
				description: 'Ability to view notes associated with issues',
			},
			UPDATE: {
				label: 'Update Issue Notes',
				action: 'issues:notes:update',
				description: 'Ability to update existing issue notes',
			},
			DELETE: {
				label: 'Delete Issue Notes',
				action: 'issues:notes:delete',
				description: 'Ability to delete existing issue notes',
			},
		},
	},
	ITEMS: {
		label: 'Items',
		CREATE: {
			label: 'Create Items',
			action: 'items:create',
			description: 'Ability to create new items',
		},
		READ: {
			label: 'View Items',
			action: 'items:read',
			description: 'Ability to view item details and lists',
		},
		UPDATE: {
			label: 'Edit Items',
			action: 'items:update',
			description: 'Ability to edit existing items',
		},
		DELETE: {
			label: 'Delete Items',
			action: 'items:delete',
			description: 'Ability to delete items',
		},
		IMAGES: {
			label: 'Item Images',
			action: 'items:images:manage',
			description: 'Ability to manage images associated with items',
		},
		INSIGHTS: {
			label: 'Item Insights',
			action: 'items:insights',
			description: 'Ability to access item insights and analytics',
		},
		NET_COST_VIEW: {
			label: 'View Net Cost',
			// Keep in sync with NET_COST_VIEW_PERMISSION in utils/net-cost.ts
			// (hard-coded there to avoid an import cycle).
			action: 'items:net_cost:view',
			description: 'Ability to view item net cost values anywhere they appear',
		},
		ITEM_LOTS: {
			label: 'Item Lots',
			READ: {
				label: 'View Item Lots',
				action: 'items:lots:read',
				description: 'Ability to view item lot details and lists',
			},
		},
		INVENTORY_MOVEMENT: {
			label: 'Inventory Movement',
			READ: {
				label: 'View Inventory Movement',
				action: 'items:inventory_movement:read',
				description:
					'Ability to view the Inventory Movement log (stock receipts, shipments, adjustments, transfers, reservations) on an item or warehouse. Intended for warehouse roles and admins.',
			},
		},
		ITEM_MATCHING: {
			label: 'Item Matching',
			action: 'items:matching',
			description:
				'Ability to match items to other items (i.e. de-duplicate items)',
		},
		DOCUMENTS: {
			label: 'Item Documents',
			READ: {
				label: 'View Item Documents',
				action: 'items:documents:read',
				description: 'Ability to view documents associated with items',
			},
			CREATE: {
				label: 'Upload Item Documents',
				action: 'items:documents:create',
				description: 'Ability to upload documents to items',
			},
			DELETE: {
				label: 'Delete Item Documents',
				action: 'items:documents:delete',
				description: 'Ability to delete documents from items',
			},
		},
	},
	LICENSES: {
		label: 'Licenses',
		READ: {
			label: 'View Licenses',
			action: 'licenses:read',
			description: 'Ability to view license details and lists',
		},
		CREATE: {
			label: 'Create Licenses',
			action: 'licenses:create',
			description: 'Ability to create new licenses',
		},
		UPDATE: {
			label: 'Update Licenses',
			action: 'licenses:update',
			description: 'Ability to update existing licenses',
		},
		DELETE: {
			label: 'Delete Licenses',
			action: 'licenses:delete',
			description: 'Ability to delete licenses',
		},
		VIEW_SELF: {
			label: 'View Assigned Licenses',
			action: 'licenses:view:self',
			description:
				'Can view licenses where user is watcher or client sales rep',
		},
		VIEW_ANY: {
			label: 'View All Licenses',
			action: 'licenses:view:any',
			description:
				'Can view all licenses regardless of ownership (bypasses ownership restrictions)',
		},
		STATUS: {
			label: 'Update License Status',
			action: 'licenses:status:update',
			description: 'Ability to update license status',
		},
		NOTES: {
			label: 'License Notes',
			CREATE: {
				label: 'Create License Notes',
				action: 'licenses:notes:create',
				description: 'Ability to create notes for licenses',
			},
			UPDATE: {
				label: 'Update License Notes',
				action: 'licenses:notes:update',
				description: 'Ability to update notes for licenses',
			},
			DELETE: {
				label: 'Delete License Notes',
				action: 'licenses:notes:delete',
				description: 'Ability to delete notes for licenses',
			},
		},
		DISCIPLINARY: {
			label: 'Disciplinary Actions',
			CREATE: {
				label: 'Create Disciplinary Actions',
				action: 'licenses:disciplinary:create',
				description: 'Ability to create disciplinary actions for licenses',
			},
			UPDATE: {
				label: 'Update Disciplinary Actions',
				action: 'licenses:disciplinary:update',
				description: 'Ability to update disciplinary actions for licenses',
			},
			DELETE: {
				label: 'Delete Disciplinary Actions',
				action: 'licenses:disciplinary:delete',
				description: 'Ability to delete disciplinary actions for licenses',
			},
		},
	},
	LISTS: {
		label: 'Lists',
		EXPORT: {
			label: 'Export List to CSV',
			action: 'lists:export',
			description: 'Ability to export list data to CSV from any list view',
		},
		IMPORT: {
			label: 'Import Records from File',
			action: 'lists:import',
			description:
				'Ability to import records from a CSV/Excel file on list views whose entity supports import. The import itself additionally requires the entity create permissions.',
		},
		SHARE_SAVED_SEARCHES: {
			label: 'Share Saved Searches',
			action: 'lists:saved-searches:share',
			description:
				'Ability to share saved searches (list tabs) with all users or specific roles. Users without this permission can only create private saved searches.',
		},
	},
	LOCATIONS: {
		label: 'Locations',
		CREATE: {
			label: 'Create Locations',
			action: 'locations:create',
			description: 'Ability to create new locations',
		},
		READ: {
			label: 'View Locations',
			action: 'locations:read',
			description: 'Ability to view location details and lists',
		},
		UPDATE: {
			label: 'Edit Locations',
			action: 'locations:update',
			description: 'Ability to edit existing locations',
		},
		DELETE: {
			label: 'Delete Locations',
			action: 'locations:delete',
			description: 'Ability to delete locations',
		},
		BULK_CREATE: {
			label: 'Bulk Create Locations',
			action: 'locations:bulk_create',
			description: 'Ability to create multiple locations at once',
		},
		BULK_UPDATE: {
			label: 'Bulk Update Locations',
			action: 'locations:bulk_update',
			description: 'Ability to update multiple locations at once',
		},
		BULK_DELETE: {
			label: 'Bulk Delete Locations',
			action: 'locations:bulk_delete',
			description: 'Ability to delete multiple locations at once',
		},
		ITEMS: {
			label: 'Location Items',
			CREATE: {
				label: 'Add Items to Locations',
				action: 'locations:items:create',
				description: 'Ability to add items to locations',
			},
			READ: {
				label: 'View Location Items',
				action: 'locations:items:read',
				description: 'Ability to view items at locations',
			},
			UPDATE: {
				label: 'Update Location Items',
				action: 'locations:items:update',
				description: 'Ability to update item quantities at locations',
			},
			DELETE: {
				label: 'Remove Items from Locations',
				action: 'locations:items:delete',
				description: 'Ability to remove items from locations',
			},
			BULK_CREATE: {
				label: 'Bulk Add Items to Locations',
				action: 'locations:items:bulk_create',
				description: 'Ability to add multiple items to locations at once',
			},
		},
		QUARANTINE_RELEASE: {
			label: 'Release from Quarantine',
			action: 'locations:quarantine_release',
			description:
				'Ability to move inventory out of a QUARANTINE-subType bin (release back to a normal sellable bin, return to vendor, or write off). Granted to Receiving Manager + Admin.',
		},
	},
	OPPORTUNITIES: {
		label: 'Opportunities',
		CREATE: {
			label: 'Create Opportunities',
			action: 'opportunities:create',
			description: 'Ability to create new opportunities',
		},
		READ: {
			label: 'View Opportunities',
			action: 'opportunities:read',
			description: 'Ability to view opportunity details and lists',
		},
		UPDATE: {
			label: 'Edit Opportunities',
			action: 'opportunities:update',
			description: 'Ability to edit existing opportunities',
		},
		DELETE: {
			label: 'Delete Opportunities',
			action: 'opportunities:delete',
			description: 'Ability to delete opportunities',
		},
		STAGES: {
			label: 'Opportunity Stages',
			MANAGE: {
				label: 'Manage Opportunity Stages',
				action: 'opportunities:stages:manage',
				description:
					'Ability to create, edit, delete, and reorder opportunity stages',
			},
		},
	},
	PRICE_CONTRACTS: {
		label: 'Price Contracts',
		CREATE: {
			label: 'Create Price Contracts',
			action: 'price_contracts:create',
			description: 'Ability to create new price contracts',
		},
		READ_ALL: {
			label: 'View All Price Contracts',
			action: 'price_contracts:read:all',
			description:
				'Ability to view every price contract in the system — its details and lists — regardless of which clients are on it.',
		},
		READ_ASSIGNED: {
			label: 'View Assigned Price Contracts',
			action: 'price_contracts:read:assigned',
			description:
				'Ability to view only price contracts that include at least one client assigned to the user (as primary or alternate salesperson). Users granted this permission but not "View All Price Contracts" will not see contracts that have none of their clients on them.',
		},
		UPDATE: {
			label: 'Edit Price Contracts',
			action: 'price_contracts:update',
			description: 'Ability to edit existing price contracts',
		},
		DELETE_ANY: {
			label: 'Delete All Price Contracts',
			action: 'price_contracts:delete:any',
			description:
				'Ability to delete any price contract created in the CRM, regardless of who created it.',
		},
		DELETE_SELF: {
			label: 'Delete Own Price Contracts',
			action: 'price_contracts:delete:self',
			description:
				'Ability to delete only price contracts the user created in the CRM. Users granted this permission but not "Delete All Price Contracts" cannot delete contracts created by someone else.',
		},
		ITEMS: {
			label: 'Price Contract Items',
			CREATE: {
				label: 'Add Items to Price Contracts',
				action: 'price_contracts:items:create',
				description: 'Ability to add items to price contracts',
			},
			READ: {
				label: 'View Price Contract Items',
				action: 'price_contracts:items:read',
				description: 'Ability to view items associated with price contracts',
			},
			UPDATE: {
				label: 'Edit Price Contract Items',
				action: 'price_contracts:items:update',
				description: 'Ability to edit item pricing in price contracts',
			},
			DELETE: {
				label: 'Remove Items from Price Contracts',
				action: 'price_contracts:items:delete',
				description: 'Ability to remove items from price contracts',
			},
		},
		CLIENTS: {
			label: 'Price Contract Clients',
			CREATE: {
				label: 'Add Clients to Price Contracts',
				action: 'price_contracts:clients:create',
				description: 'Ability to add clients to price contracts',
			},
			READ: {
				label: 'View Price Contract Clients',
				action: 'price_contracts:clients:read',
				description: 'Ability to view clients associated with price contracts',
			},
			UPDATE: {
				label: 'Edit Price Contract Clients',
				action: 'price_contracts:clients:update',
				description: 'Ability to edit client assignments in price contracts',
			},
			DELETE: {
				label: 'Remove Clients from Price Contracts',
				action: 'price_contracts:clients:delete',
				description: 'Ability to remove clients from price contracts',
			},
		},
		SAVED_PRICES: {
			label: 'Saved Prices',
			CREATE: {
				label: 'Create Saved Prices Contract',
				action: 'price_contracts:saved_prices:create',
				description: 'Ability to create saved prices contracts for clients',
			},
			OVERRIDE: {
				label: 'Override Contract Prices',
				action: 'price_contracts:saved_prices:override',
				description:
					'Ability to set saved prices to override other contract prices',
			},
			BELOW_MINIMUM: {
				label: 'Price Below Minimum',
				action: 'price_contracts:saved_prices:below_minimum',
				description: 'Ability to save prices below the item minimum price',
			},
		},
		COSTS: {
			label: 'Price Contract Costs',
			VIEW: {
				label: 'View Price Contract Costs',
				action: 'price_contracts:costs:view',
				description:
					'Ability to view contract-driven acquisition and sell costs on price contracts, the shop/cart, and order details',
			},
			EDIT: {
				label: 'Edit Price Contract Costs',
				action: 'price_contracts:costs:edit',
				description:
					'Ability to configure contract-driven acquisition and sell costs on price contract items',
			},
		},
	},
	PROMOS: {
		label: 'Promos',
		READ: {
			label: 'View Promos',
			action: 'promos:read',
			description:
				'Ability to view promotions, their configurations, and redemption activity',
		},
		EDIT: {
			label: 'Manage Promos',
			action: 'promos:edit',
			description:
				'Ability to create, update, and delete promotions; assign items, manage targeting, and upload promo images',
		},
	},
	ORDERS: {
		label: 'Orders',
		CREATE: {
			label: 'Create Orders',
			action: 'orders:create',
			description: 'Ability to create new orders',
		},
		VIEW_OWN: {
			label: 'View Own Orders',
			action: 'orders:view:own',
			description:
				'Can view orders for clients the user owns (assigned salesperson, alternate, or unassigned when paired with clients:view:unassigned).',
		},
		VIEW_ANY: {
			label: 'View All Orders',
			action: 'orders:view:any',
			description:
				'Can view orders for any client regardless of sales rep assignment (bypasses ownership restrictions).',
		},
		UPDATE: {
			label: 'Edit Orders',
			action: 'orders:update',
			description: 'Ability to edit existing orders',
		},
		DELETE: {
			label: 'Delete Orders',
			action: 'orders:delete',
			description: 'Ability to delete orders',
		},
		CANCEL: {
			label: 'Cancel Orders',
			action: 'orders:cancel',
			description: 'Ability to cancel orders',
		},
		APPROVE: {
			label: 'Approve Orders',
			action: 'orders:approve',
			description: 'Ability to approve orders that require approval',
		},
		REJECT: {
			label: 'Reject Orders',
			action: 'orders:reject',
			description: 'Ability to reject orders that require approval',
		},
		EXPORT: {
			label: 'Export Orders',
			action: 'orders:export',
			description: 'Ability to export order data',
		},
		EMAIL: {
			label: 'Email Orders',
			action: 'orders:email',
			description: 'Ability to email order details',
		},
		REORDER: {
			label: 'Reorder Items',
			action: 'orders:reorder',
			description: 'Ability to reorder items from previous orders',
		},
		RESEND_TO_ERP: {
			label: 'Resend Orders to ERP',
			action: 'orders:resend_to_erp',
			description: 'Ability to resend orders to ERP system (admin only)',
		},
		NOTES: {
			label: 'Order Notes',
			CREATE: {
				label: 'Create Order Notes',
				action: 'orders:notes:create',
				description: 'Ability to create notes for orders',
			},
			READ: {
				label: 'View Order Notes',
				action: 'orders:notes:read',
				description: 'Ability to view notes associated with orders',
			},
			UPDATE_SELF: {
				label: 'Update Own Order Notes',
				action: 'orders:notes:update:self',
				description: 'Ability to update notes you created for orders',
			},
			UPDATE_ANY: {
				label: 'Update Any Order Notes',
				action: 'orders:notes:update:any',
				description: 'Ability to update any notes for orders',
			},
			DELETE_SELF: {
				label: 'Delete Own Order Notes',
				action: 'orders:notes:delete:self',
				description: 'Ability to delete notes you created for orders',
			},
			DELETE_ANY: {
				label: 'Delete Any Order Notes',
				action: 'orders:notes:delete:any',
				description: 'Ability to delete any notes for orders',
			},
		},
		GP: {
			label: 'Gross Profit',
			VIEW: {
				label: 'View Gross Profit',
				action: 'orders:gp:view',
				description: 'Ability to view gross profit',
			},
		},
		FULFILLMENT: {
			label: 'Fulfillment',
			VIEW: {
				label: 'View Fulfillment Queue',
				action: 'orders:fulfillment:view',
				description:
					'Ability to view the cross-client fulfillment queue (pending groups, items, carriers, and picking progress). Separate from orders:view:* because the fulfillment queue spans all clients regardless of ownership — a warehouse picker needs queue visibility without broad order-read access elsewhere.',
			},
			PICK: {
				label: 'Pick Fulfillment Items',
				action: 'orders:fulfillment:pick',
				description:
					'Ability to perform warehouse-floor picking actions on the fulfillment queue: pick, unpick, mark short, undo backorder, undo skip, and complete picking for a group. Does not grant the ability to finalize shipments or invoices — that still requires invoices:create.',
			},
		},
	},
	QUOTES: {
		label: 'Quotes',
		CREATE: {
			label: 'Create Quotes',
			action: 'quotes:create',
			description: 'Ability to create new quotes',
		},
		VIEW_OWN: {
			label: 'View Own Quotes',
			action: 'quotes:view:own',
			description:
				'Can view quotes for clients the user owns (assigned salesperson, alternate, or unassigned when paired with clients:view:unassigned).',
		},
		VIEW_ANY: {
			label: 'View All Quotes',
			action: 'quotes:view:any',
			description:
				'Can view quotes for any client regardless of sales rep assignment (bypasses ownership restrictions).',
		},
		UPDATE: {
			label: 'Edit Quotes',
			action: 'quotes:update',
			description: 'Ability to edit existing quotes',
		},
		DELETE: {
			label: 'Delete Quotes',
			action: 'quotes:delete',
			description: 'Ability to delete quotes',
		},
		EXPORT: {
			label: 'Export Quotes',
			action: 'quotes:export',
			description: 'Ability to export quote data',
		},
		EMAIL: {
			label: 'Email Quotes',
			action: 'quotes:email',
			description: 'Ability to email quotes',
		},
		MOVE_TO_CART: {
			label: 'Move Quotes to Cart',
			action: 'quotes:move_to_cart',
			description: 'Ability to move quotes to shopping cart for order creation',
		},
		ADD_NON_INVENTORY_ITEMS: {
			label: 'Add Non-Inventory Items to Quotes',
			action: 'quotes:add_non_inventory_items',
			description: 'Ability to add non-inventory items to quotes',
		},
	},
	RECON: {
		label: 'Recon',
		READ: {
			label: 'View Recon',
			action: 'recon:read',
			description: 'Ability to view Recon opportunities and signals',
		},
		OPPORTUNITIES: {
			label: 'Opportunities',
			READ: {
				label: 'View Opportunities',
				action: 'recon:opportunities:read',
				description: 'Ability to view Recon opportunities queue',
			},
			APPROVE: {
				label: 'Approve Interactions',
				action: 'recon:opportunities:approve',
				description: 'Ability to approve AI-drafted interactions for sending',
			},
			DISMISS: {
				label: 'Dismiss Opportunities',
				action: 'recon:opportunities:dismiss',
				description: 'Ability to dismiss or snooze opportunities',
			},
		},
		INTERACTIONS: {
			label: 'Interactions',
			EDIT: {
				label: 'Edit Drafts',
				action: 'recon:interactions:edit',
				description: 'Ability to edit AI-drafted content before sending',
			},
			SEND: {
				label: 'Send Interactions',
				action: 'recon:interactions:send',
				description: 'Ability to send approved interactions to platforms',
			},
		},
		PLATFORMS: {
			label: 'Platform Accounts',
			READ: {
				label: 'View Platform Accounts',
				action: 'recon:platforms:read',
				description: 'Ability to view connected platform accounts',
			},
			MANAGE: {
				label: 'Manage Platform Accounts',
				action: 'recon:platforms:manage',
				description:
					'Ability to connect/disconnect platform accounts and manage API keys',
			},
		},
		SETTINGS: {
			label: 'Settings',
			READ: {
				label: 'View Recon Settings',
				action: 'recon:settings:read',
				description: 'Ability to view Recon configuration',
			},
			MANAGE: {
				label: 'Manage Recon Settings',
				action: 'recon:settings:manage',
				description:
					'Ability to configure company profile, targeting rules, and behavior settings',
			},
		},
		CONTACTS: {
			label: 'Contact Mapping',
			LINK: {
				label: 'Link Social Profiles',
				action: 'recon:contacts:link',
				description: 'Ability to link/unlink social profiles to CRM contacts',
			},
		},
	},
	RBAC: {
		label: 'RBAC',
		USER: {
			label: 'User',
			ACTIONS: {
				label: 'Direct Actions',
				CREATE: {
					label: 'Assign Direct Actions',
					action: 'rbac:user:actions:assign',
					description: 'Ability to assign direct actions to users',
				},
				READ: {
					label: 'View Direct Actions',
					action: 'rbac:user:actions:view',
					description: 'Ability to view direct actions assigned to users',
				},
				DELETE: {
					label: 'Remove Direct Actions',
					action: 'rbac:user:actions:remove',
					description: 'Ability to remove direct actions assigned to users',
				},
			},
			ROLES: {
				label: 'Roles',
				CREATE: {
					label: 'Assign Roles',
					action: 'rbac:user:roles:assign',
					description: 'Ability to assign roles to users',
				},
				READ: {
					label: 'View Roles',
					action: 'rbac:user:roles:view',
					description: 'Ability to view roles assigned to users',
				},
				DELETE: {
					label: 'Remove Roles',
					action: 'rbac:user:roles:remove',
					description: 'Ability to remove roles assigned to users',
				},
			},
		},
		ROLE: {
			label: 'Role',
			ACTIONS: {
				label: 'Actions',
				CREATE: {
					label: 'Assign Actions',
					action: 'rbac:role:actions:assign',
					description: 'Ability to assign actions to roles',
				},
				READ: {
					label: 'View Actions',
					action: 'rbac:role:actions:view',
					description: 'Ability to view actions assigned to roles',
				},
				DELETE: {
					label: 'Remove Actions',
					action: 'rbac:role:actions:remove',
					description: 'Ability to remove actions assigned to roles',
				},
			},
			USERS: {
				label: 'Users',
				action: 'rbac:users:read',
				description: 'Ability to view users assigned to roles',
			},
		},
	},
	REPORTING: {
		label: 'Reporting',
		SALES: {
			label: 'Sales Reports',
			SALES_ANALYSIS: {
				label: 'Sales Analysis',
				action: 'reporting:sales:analysis',
				description: 'Ability to access sales analysis reports',
			},
			COMPANY_BREAKDOWN: {
				label: 'Company Breakdown',
				action: 'reporting:sales:company_breakdown',
				description: 'Ability to access company breakdown sales report',
			},
			SALES_PIPELINE: {
				label: 'Sales Pipeline',
				action: 'reporting:sales:sales_pipeline',
				description: 'Ability to access sales pipeline report',
			},
			TOP_PERFORMERS: {
				label: 'Top Performers',
				action: 'reporting:sales:top_performers',
				description: 'Ability to access top performers report',
			},
			SALES_BY_PRODUCT: {
				label: 'Sales by Product',
				action: 'reporting:sales:sales_by_product',
				description: 'Ability to access sales by product report',
			},
			SALES_BY_COMPANY: {
				label: 'Sales by Company',
				action: 'reporting:sales:sales_by_company',
				description: 'Ability to access sales by company report',
			},
			SALES_BY_ORIGIN: {
				label: 'Sales by Origin',
				action: 'reporting:sales:sales_by_origin',
				description: 'Ability to access sales by origin report',
			},
			SALES_BY_PRODUCT_WITH_COMPANY_DETAILS: {
				label: 'Sales by Product with Company Details',
				action: 'reporting:sales:sales_by_product_with_company_details',
				description:
					'Ability to access sales by product with company details report',
			},
			ITEM_PERFORMANCE_ANALYSIS: {
				label: 'Item Performance Analysis',
				action: 'reporting:sales:item_performance_analysis',
				description: 'Ability to access item performance analysis report',
			},
			PRODUCT_CATEGORY_DISTRIBUTION: {
				label: 'Product Category Distribution',
				action: 'reporting:sales:product_category_distribution',
				description: 'Ability to access product category distribution report',
			},
			PROMO_REDEMPTIONS: {
				label: 'Promo Redemptions',
				action: 'reporting:sales:promo_redemptions',
				description:
					'Ability to access the Promo Redemptions report — per-line per-promo discount + cost-adjustment history with date / customer / coupon filters',
			},
			// Was previously REPORTING.OPERATIONS.ACTIVE_ORDERS; the report was
			// generalized into "Orders" and reclassified as a sales report. The
			// action string is preserved so existing role grants keep working
			// without a re-seed migration.
			ORDERS: {
				label: 'Orders',
				action: 'reporting:operations:active_orders',
				description: 'Ability to access the Orders report',
			},
		},
		INVENTORY: {
			label: 'Inventory Reports',
			STOCK_LEVELS: {
				label: 'Stock Levels',
				action: 'reporting:inventory:stock_levels',
				description: 'Ability to access stock levels report',
			},
			PRODUCT_MOVEMENT: {
				label: 'Product Movement',
				action: 'reporting:inventory:product_movement',
				description: 'Ability to access product movement report',
			},
			INVENTORY_VALUATION: {
				label: 'Inventory Valuation',
				action: 'reporting:inventory:inventory_valuation',
				description: 'Ability to access inventory valuation report',
			},
			REORDER_ALERTS_INSIGHTS: {
				label: 'Reorder Alert Insights',
				action: 'reporting:inventory:reorder_alerts_insights',
				description: 'Ability to access reorder alert insights report',
			},
			AT_RISK_INSIGHTS: {
				label: 'At Risk Insights',
				action: 'reporting:inventory:at_risk_insights',
				description: 'Ability to access at risk insights report',
			},
			COMPANY_REORDER_INSIGHTS: {
				label: 'Company Reorder Insights',
				action: 'reporting:inventory:company_reorder_insights',
				description: 'Ability to access company reorder insights report',
			},
			COMPANY_AT_RISHK_INSIGHTS: {
				label: 'Company At Risk Insights',
				action: 'reporting:inventory:company_at_risk_insights',
				description: 'Ability to access company at risk insights report',
			},
		},
		CUSTOMER: {
			label: 'Customer Reports',
			CUSTOMER_ANALYTICS: {
				label: 'Customer Analytics',
				action: 'reporting:customer:customer_analytics',
				description: 'Ability to access customer analytics report',
			},
			CUSTOMER_RETENTION: {
				label: 'Customer Retention',
				action: 'reporting:customer:customer_retention',
				description: 'Ability to access customer retention report',
			},
			CUSTOMER_LIFETIME_VALUE: {
				label: 'Customer Lifetime Value',
				action: 'reporting:customer:customer_lifetime_value',
				description: 'Ability to access customer lifetime value report',
			},
			NEW_CLIENTS: {
				label: 'New Clients',
				action: 'reporting:customer:new_clients',
				description: 'Ability to access new clients report',
			},
			CLIENT_PAYMENT_STATUS: {
				label: 'Client Payment Status',
				action: 'reporting:customer:client_payment_status',
				description: 'Ability to access client payment status report',
			},
			TOP_CLIENTS_BY_REVENUE: {
				label: 'Top Clients by Revenue',
				action: 'reporting:customer:top_clients_by_revenue',
				description: 'Ability to access top clients by revenue report',
			},
			CUSTOMER_REORDER_REPORT: {
				label: 'Customer Reorder Report',
				action: 'reporting:customer:customer_reorder_report',
				description: 'Ability to access customer reorder report',
			},
			CUSTOMER_SAVED_PRICES: {
				label: 'Customer Saved Prices',
				action: 'reporting:customer:customer_saved_prices',
				description: 'Ability to access customer saved prices report',
			},
		},
		GEOGRAPHIC: {
			label: 'Geographic Reports',
			REGIONAL_SALES: {
				label: 'Regional Sales',
				action: 'reporting:geographic:regional_sales',
				description: 'Ability to access regional sales report',
			},
			TERRITORY_ANALYSIS: {
				label: 'Territory Analysis',
				action: 'reporting:geographic:territory_analysis',
				description: 'Ability to access territory analysis report',
			},
			SHIPPING_ZONE_ANALYSIS: {
				label: 'Shipping Zone Analysis',
				action: 'reporting:geographic:shipping_zone_analysis',
				description: 'Ability to access shipping zone analysis report',
			},
			MARKET_PENETRATION: {
				label: 'Market Penetration',
				action: 'reporting:geographic:market_penetration',
				description: 'Ability to access market penetration report',
			},
		},
		OPERATIONS: {
			label: 'Operations Reports',
			PENDING_INVOICES: {
				label: 'Pending Invoices',
				action: 'reporting:operations:pending_invoices',
				description: 'Ability to access pending invoices report',
			},
			ABANDONED_CARTS: {
				label: 'Abandoned Carts',
				action: 'reporting:operations:abandoned_carts',
				description: 'Ability to access abandoned carts report',
			},
			QUOTES: {
				label: 'Quotes',
				action: 'reporting:operations:quotes',
				description: 'Ability to access quotes report',
			},
			ITEM_SEARCH_ACTIVITY: {
				label: 'Item Search Activity',
				action: 'reporting:operations:item_search_activity',
				description: 'Ability to access the item search activity report',
			},
		},
		ACCOUNTING: {
			label: 'Accounting Reports',
			ACCOUNTS_RECEIVABLE_AGING: {
				label: 'Accounts Receivable Aging',
				action: 'reporting:accounting:accounts_receivable_aging',
				description: 'Ability to access accounts receivable aging report',
			},
			TAX_SUMMARY: {
				label: 'Tax Summary',
				action: 'reporting:accounting:tax_summary',
				description: 'Ability to access tax summary report',
			},
			CASH_RECEIPTS: {
				label: 'Cash Receipts',
				action: 'reporting:accounting:cash_receipts',
				description: 'Ability to access cash receipts report',
			},
			ORDER_RECONCILIATION: {
				label: 'Order Reconciliation',
				action: 'reporting:accounting:order_reconciliation',
				description:
					'Ability to access the order reconciliation report (delivery, billing and payment state per order, with payments shown in order context)',
			},
			GROSS_PROFIT_DETAIL: {
				label: 'Gross Profit Detail',
				action: 'reporting:accounting:gross_profit_detail',
				description: 'Ability to access gross profit detail report',
			},
			CREDIT_MANAGEMENT: {
				label: 'Credit Management',
				action: 'reporting:accounting:credit_management',
				description: 'Ability to access credit management report',
			},
			CUSTOMER_STATEMENTS: {
				label: 'Customer Statements',
				action: 'reporting:accounting:customer_statements',
				description:
					'Ability to generate, download, and email customer statements',
			},
		},
		QUOTE_ANALYSIS: {
			label: 'Quote Analysis Reports',
			QUOTE_ITEM_DEMAND_SUMMARY: {
				label: 'Quote Item Demand Summary',
				action: 'reporting:quote_analysis:quote_item_demand_summary',
				description: 'Ability to access quote item demand summary report',
			},
			QUOTE_DEMAND_BY_CUSTOMER: {
				label: 'Quote Demand by Customer',
				action: 'reporting:quote_analysis:quote_demand_by_customer',
				description: 'Ability to access quote demand by customer report',
			},
		},
		RECEIVING: {
			label: 'Receiving Reports',
			OPEN_RECEIPT_ISSUES: {
				label: 'Open Receipt Issues',
				action: 'reporting:receiving:open_receipt_issues',
				description:
					'Ability to access the Open Receipt Issues widget — count + drilldown of receipt-detail exceptions in non-terminal states (R1).',
			},
			RECEIPT_VOLUME_THIS_WEEK: {
				label: 'Receipt Volume This Week',
				action: 'reporting:receiving:receipt_volume_this_week',
				description:
					'Ability to access the Receipt Volume This Week widget — line chart of receipts created per day for the last 7 days (R2).',
			},
			AVERAGE_TIME_TO_ACKNOWLEDGE: {
				label: 'Average Time to Acknowledge',
				action: 'reporting:receiving:average_time_to_acknowledge',
				description:
					'Ability to access the Average Time to Acknowledge widget — average minutes from dockArrivalAt to receipt _created_date over last 30d (R3).',
			},
			EXCEPTIONS_BY_TYPE_30D: {
				label: 'Exceptions by Type (30d)',
				action: 'reporting:receiving:exceptions_by_type_30d',
				description:
					'Ability to access the Exceptions by Type widget — bar chart counting receipt exceptions by type over the last 30 days (R4).',
			},
		},
		PURCHASING: {
			label: 'Purchasing Reports',
			OPEN_SRS_BY_STATUS: {
				label: 'Open Sourcing Requests by Status',
				action: 'reporting:purchasing:open_srs_by_status',
				description:
					'Ability to access the Open SRs by Status pie-chart widget (W1)',
			},
			SR_AGING: {
				label: 'Sourcing Request Aging',
				action: 'reporting:purchasing:sr_aging',
				description: 'Ability to access the Top 10 Oldest Open SRs widget (W2)',
			},
			OUTSTANDING_VENDOR_QUOTES: {
				label: 'Outstanding Vendor Quotes',
				action: 'reporting:purchasing:outstanding_vendor_quotes',
				description:
					'Ability to access the outstanding vendor quotes widget (W3)',
			},
			QUOTES_FOLLOW_UP: {
				label: 'Quotes Due for Follow-Up',
				action: 'reporting:purchasing:quotes_follow_up',
				description:
					'Ability to access the quotes-due-for-follow-up widget (W4)',
			},
			SR_LINES_BY_PIPELINE_STAGE: {
				label: 'SR Lines by Pipeline Stage',
				action: 'reporting:purchasing:sr_lines_by_pipeline_stage',
				description:
					'Ability to access the SR lines by pipeline-stage stacked-bar widget (W5)',
			},
			TOP_VENDORS_BY_OUTSTANDING_PO_VALUE: {
				label: 'Top Vendors by Outstanding PO Value',
				action: 'reporting:purchasing:top_vendors_by_outstanding_po_value',
				description:
					'Ability to access the top-vendors-by-outstanding-PO-value widget (W6)',
			},
			OUTSTANDING_VENDOR_ISSUES_BY_STATUS: {
				label: 'Outstanding Vendor Issues by Status',
				action: 'reporting:purchasing:outstanding_vendor_issues_by_status',
				description:
					'Ability to access the Outstanding Vendor Issues by Status widget — pie chart of receipt exceptions in vendor-action statuses (P1).',
			},
			CREDIT_PENDING_AT_RISK: {
				label: 'Credit Pending — $ at Risk',
				action: 'reporting:purchasing:credit_pending_at_risk',
				description:
					'Ability to access the Credit Pending widget — sum of expectedCreditAmount across CREDIT_PENDING exceptions (P2).',
			},
			VENDOR_ISSUE_RATE: {
				label: 'Vendor Scorecard — Issue Rate',
				action: 'reporting:purchasing:vendor_issue_rate',
				description:
					'Ability to access the Vendor Issue Rate widget — top 10 vendors by % of receipts with exceptions, last 90d (P3).',
			},
			AGING_OPEN_ISSUES: {
				label: 'Aging Open Issues — Top 10',
				action: 'reporting:purchasing:aging_open_issues',
				description:
					'Ability to access the Aging Open Issues widget — oldest unresolved exceptions chase list (P4).',
			},
			POS_AWAITING_FIRST_RECEIPT: {
				label: 'POs Awaiting First Receipt',
				action: 'reporting:purchasing:pos_awaiting_first_receipt',
				description:
					'Ability to access the POs Awaiting First Receipt widget — count of RELEASED POs with no receipt (P5).',
			},
		},
		KPI_AND_STATS: {
			label: 'KPI and Stats Reports',
			KPI_OVERVIEW: {
				label: 'KPI Overview',
				action: 'reporting:kpi_and_stats:kpi_overview',
				description: 'Ability to access KPI overview report',
			},
			KPI_DETAILS: {
				label: 'KPI Details',
				action: 'reporting:kpi_and_stats:kpi_details',
				description: 'Ability to access KPI details report',
			},
		},
		DISTRIBUTION: {
			label: 'Distribution Reports',
			DRIVER_TRIPS_TODAY: {
				label: 'Your Trips Today',
				action: 'reporting:distribution:driver_trips_today',
				description:
					"Ability to see the dashboard widget listing today's trips assigned to the current user",
			},
			MY_TRIP_PROGRESS: {
				label: 'My Trip Progress',
				action: 'reporting:distribution:my_trip_progress',
				description:
					"Ability to see the dashboard widget showing the driver's current trip progress (stops done/remaining, next stop, next action)",
			},
			TODAYS_TRIPS: {
				label: "Today's Trips",
				action: 'reporting:distribution:todays_trips',
				description:
					'Ability to see the dashboard widget listing open trips and recently closed trips with their pending warehouse work',
			},
		},
		SALES_ROUTES: {
			label: 'Sales Route Reports',
			MY_SALES_ROUTE_TODAY: {
				label: 'My Sales Route Today',
				action: 'reporting:sales_routes:my_sales_route_today',
				description:
					"Ability to see the dashboard widget listing today's due stops on the current salesperson's sales routes, with quick actions (Log Visit, Skip, Snooze)",
			},
		},
		SCORECARD: {
			label: 'Scorecard Reports',
			CUSTOMER_SCORECARD: {
				label: 'Customer Scorecard',
				action: 'reporting:scorecard:customer_scorecard',
				description: 'Ability to access customer scorecard report',
			},
			ITEM_SCORECARD: {
				label: 'Item Scorecard',
				action: 'reporting:scorecard:item_scorecard',
				description: 'Ability to access item scorecard report',
			},
			SALES_REP_SCORECARD: {
				label: 'Sales Rep Scorecard',
				action: 'reporting:scorecard:sales_rep_scorecard',
				description: 'Ability to access sales rep scorecard report',
			},
			VENDOR_SCORECARD: {
				label: 'Vendor Scorecard',
				action: 'reporting:scorecard:vendor_scorecard',
				description: 'Ability to access vendor scorecard report',
			},
		},
		COMMUNICATIONS: {
			label: 'Communications Reports',
			ACTIVITY_SUMMARY: {
				label: 'Communications Activity Summary',
				action: 'reporting:communications:activity_summary',
				description:
					'Ability to access the phone-system activity summary (totals, answered/voicemail/abandoned rates) (DD.8)',
			},
			CALLS_BY_NUMBER: {
				label: 'Calls by Phone Number',
				action: 'reporting:communications:calls_by_number',
				description:
					'Ability to access per-tenant-line traffic breakdown (totals, answer rate, avg duration) (DD.8)',
			},
			TEAM_COMMUNICATIONS_OVERVIEW: {
				label: 'Team Communications Overview',
				action: 'reporting:communications:team_communications_overview',
				description:
					"Manager view: per-user totals (call count + duration, SMS sent/received, email count, AI session count) for the caller's team. Visibility scope is data-driven via the manager-hierarchy schema (UserEntity.fk_user__id_manager / fk__rbac_role__id_manager, GroupEntity.fk_group__id_parent, RbacRoleEntity.fk__rbac_role__id_manager); the action only gates whether the menu item appears.",
			},
		},
		// Report data-scope actions. These gate WHICH clients' data a user
		// sees in aggregated reports, independent of the per-report access
		// actions above and independent of client-detail permissions
		// (clients:detail:any / clients:view:*). Being able to open any
		// client's detail page does NOT imply being able to see aggregated
		// report data across all clients — that is an explicit grant here.
		// The four are additive: ANY overrides everything; SELF, ALTERNATE
		// and UNASSIGNED union together. A user with none of them sees no
		// report data.
		//
		// SELF and ALTERNATE are deliberately separate: SELF covers clients
		// where the user is the PRIMARY rep, ALTERNATE covers clients where
		// they are the ALTERNATE rep. They were split (PD-13754) so that
		// alternate-rep visibility is an explicit opt-in — no role is granted
		// ALTERNATE by default.
		DATA_SCOPE: {
			label: 'Report Data Scope',
			DATA_ANY: {
				label: 'View All Report Data',
				action: 'reporting:data:any',
				description:
					'See report data for all clients regardless of sales rep assignment. Overrides the self/alternate/unassigned data-scope actions.',
			},
			DATA_SELF: {
				label: 'View My Report Data',
				action: 'reporting:data:self',
				description:
					'See report data only for clients where the user is the primary sales rep. Combines additively with the alternate/unassigned data-scope actions.',
			},
			DATA_ALTERNATE: {
				label: 'View Alternate Report Data',
				action: 'reporting:data:alternate',
				description:
					'See report data for clients where the user is the alternate sales rep. Combines additively with "View My Report Data".',
			},
			DATA_UNASSIGNED: {
				label: 'View Unassigned Report Data',
				action: 'reporting:data:unassigned',
				description:
					'See report data for clients that have no sales rep assigned. Combines additively with "View My Report Data".',
			},
		},
	},
	PURCHASE_ORDERS: {
		label: 'Purchase Orders',
		CREATE: {
			label: 'Create Purchase Orders',
			action: 'purchase_orders:create',
			description: 'Ability to create new purchase orders',
		},
		READ: {
			label: 'View Purchase Orders',
			action: 'purchase_orders:read',
			description: 'Ability to view purchase order details and lists',
		},
		UPDATE: {
			label: 'Edit Purchase Orders',
			action: 'purchase_orders:update',
			description: 'Ability to edit existing purchase orders',
		},
		DELETE: {
			label: 'Delete Purchase Orders',
			action: 'purchase_orders:delete',
			description: 'Ability to delete purchase orders',
		},
		RECEIPTS: {
			label: 'Purchase Order Receipts',
			CREATE: {
				label: 'Create Receipts',
				action: 'purchase_orders:receipts:create',
				description: 'Ability to create receipts for purchase orders',
			},
			READ: {
				label: 'View Receipts',
				action: 'purchase_orders:receipts:read',
				description: 'Ability to view receipts for purchase orders',
			},
			UPDATE: {
				label: 'Edit Receipts',
				action: 'purchase_orders:receipts:update',
				description: 'Ability to edit receipts for purchase orders',
			},
			DELETE: {
				label: 'Delete Receipts',
				action: 'purchase_orders:receipts:delete',
				description: 'Ability to delete receipts for purchase orders',
			},
			OVER_SHIP_OVERRIDE: {
				label: 'Override Over-Shipment Tolerance',
				action: 'purchase_orders:receipts:over_ship_override',
				description:
					'Ability to save a receipt with quantity received exceeding the per-tenant over-ship tolerance. Granted to Receiving Manager + Admin. Default tolerance is 10% over PO ordered quantity.',
			},
			EXCEPTIONS: {
				label: 'Receipt Exceptions',
				READ: {
					label: 'View Receipt Exceptions',
					action: 'purchase_orders:receipts:exceptions:read',
					description:
						'Ability to view receipt exceptions (damaged, short, over, refused, etc.) — required for the Outstanding Vendor Issues tab on POs and the Open Issues tab on Receipts.',
				},
				CREATE: {
					label: 'Create Receipt Exceptions',
					action: 'purchase_orders:receipts:exceptions:create',
					description:
						'Ability to record exception lines on a receipt (auto-fired when a receiving clerk uses the disposition splitter).',
				},
				UPDATE: {
					label: 'Update Receipt Exceptions',
					action: 'purchase_orders:receipts:exceptions:update',
					description:
						'Ability to edit assignee, notes, vendor claim number, and credit amounts on an exception. Does not include status transitions.',
				},
				NOTIFY_VENDOR: {
					label: 'Notify Vendor of Exception',
					action: 'purchase_orders:receipts:exceptions:notify_vendor',
					description:
						'Ability to send a vendor-claim email for a receipt exception. Transitions OPEN → VENDOR_NOTIFIED. Granted to Receiving Manager + Purchasing + Purchasing Manager + Admin.',
				},
				RESOLVE: {
					label: 'Resolve Receipt Exceptions',
					action: 'purchase_orders:receipts:exceptions:resolve',
					description:
						'Ability to close out an exception (RESOLVED or WRITTEN_OFF transitions). Granted to Receiving Manager + Purchasing Manager + Admin — both manager roles can resolve because resolution can come from either side (clerk miskey vs vendor credit).',
				},
			},
		},
		RELEASE: {
			label: 'Release Purchase Orders',
			action: 'purchase_orders:release',
			description:
				'Ability to release a purchase order from NEW or QUOTE status to RELEASED, committing it as a binding order with the vendor',
		},
		SEND_EMAIL: {
			label: 'Email Purchase Orders',
			action: 'purchase_orders:send_email',
			description:
				'Ability to send purchase order or RFQ emails to vendors via the EmailService',
		},
		APPROVE: {
			label: 'Approve Purchase Orders',
			action: 'purchase_orders:approve',
			description:
				'Ability to approve or reject a purchase order that was submitted for approval (e.g. exceeds the tenant PO total threshold). Granted to Purchasing Manager + Admin.',
		},
	},
	ROLES: {
		label: 'Roles',
		CREATE: {
			label: 'Create Roles',
			action: 'rbac:roles:create',
			description: 'Ability to create new roles',
		},
		READ: {
			label: 'View Roles',
			action: 'rbac:roles:view',
			description:
				'Ability to view existing roles, their actions, and users with those roles',
		},
		UPDATE: {
			label: 'Update Roles',
			action: 'rbac:roles:update',
			description: 'Ability to update existing roles',
		},
		DELETE: {
			label: 'Delete Roles',
			action: 'rbac:roles:delete',
			description: 'Ability to delete existing roles',
		},
	},
	STATEMENTS: {
		label: 'Statements',
		VIEW: {
			label: 'View Statements',
			action: 'statements:view',
			description:
				'Ability to view stored customer statements (list, detail, PDF download). Company-scoped via company access.',
		},
		CREATE: {
			label: 'Create Statements',
			action: 'statements:create',
			description:
				'Ability to generate customer statements (one-off or via statement runs) and email them.',
		},
	},
	TASKS: {
		label: 'Tasks',
		CREATE_SELF: {
			label: 'Create Own Tasks',
			action: 'tasks:create:self',
			description: 'Ability to create tasks assigned to yourself',
		},
		CREATE_ANY: {
			label: 'Create Tasks for Any User',
			action: 'tasks:create:any',
			description: 'Ability to create tasks assigned to any user',
		},
		READ_SELF: {
			label: 'View Own Tasks',
			action: 'tasks:view:self',
			description: 'Ability to view tasks assigned to yourself',
		},
		READ_ANY: {
			label: 'View All Tasks',
			action: 'tasks:view:any',
			description: 'Ability to view tasks assigned to any user',
		},
		UPDATE_SELF: {
			label: 'Update Own Tasks',
			action: 'tasks:update:self',
			description: 'Ability to update tasks assigned to yourself',
		},
		UPDATE_ANY: {
			label: 'Update Any Tasks',
			action: 'tasks:update:any',
			description: 'Ability to update tasks assigned to any user',
		},
		DELETE_SELF: {
			label: 'Delete Own Tasks',
			action: 'tasks:delete:self',
			description: 'Ability to delete tasks assigned to yourself',
		},
		DELETE_ANY: {
			label: 'Delete Any Tasks',
			action: 'tasks:delete:any',
			description: 'Ability to delete tasks assigned to any user',
		},
		COMPLETE_SELF: {
			label: 'Complete Own Tasks',
			action: 'tasks:complete:self',
			description: 'Ability to mark tasks assigned to yourself as complete',
		},
		COMPLETE_ANY: {
			label: 'Complete Any Tasks',
			action: 'tasks:complete:any',
			description: 'Ability to mark tasks assigned to any user as complete',
		},
	},
	TEAMS: {
		label: 'Teams',
		CREATE: {
			label: 'Create Teams',
			action: 'teams:create',
			description: 'Ability to create new teams',
		},
		READ: {
			label: 'View Teams',
			action: 'teams:read',
			description: 'Ability to view team details and lists',
		},
		UPDATE: {
			label: 'Edit Teams',
			action: 'teams:update',
			description: 'Ability to edit existing teams',
		},
		DELETE: {
			label: 'Delete Teams',
			action: 'teams:delete',
			description: 'Ability to delete teams',
		},
	},
	TRANSACTIONS: {
		label: 'Transactions',
		CREATE: {
			label: 'Create Transactions',
			action: 'transactions:create',
			description: 'Ability to create new transactions',
		},
		VIEW_OWN: {
			label: 'View Own Transactions',
			action: 'transactions:view:own',
			description:
				'Can view transactions for clients the user owns (assigned salesperson, alternate, or unassigned when paired with clients:view:unassigned).',
		},
		VIEW_ANY: {
			label: 'View All Transactions',
			action: 'transactions:view:any',
			description:
				'Can view transactions for any client regardless of sales rep assignment (bypasses ownership restrictions).',
		},
		UPDATE: {
			label: 'Edit Transactions',
			action: 'transactions:update',
			description: 'Ability to edit existing transactions',
		},
		VOID: {
			label: 'Void Transactions',
			action: 'transactions:void',
			description: 'Ability to void existing transactions',
		},
		REFUND: {
			label: 'Refund Transactions',
			action: 'transactions:refund',
			description: 'Ability to refund existing transactions',
		},
		PAYMENT_SCHEDULES: {
			label: 'Payment Schedules',
			CREATE: {
				label: 'Create Payment Schedules',
				action: 'transactions:payment_schedules:create',
				description:
					'Ability to create autopay, recurring, and installment payment schedules',
			},
			READ: {
				label: 'View Payment Schedules',
				action: 'transactions:payment_schedules:read',
				description:
					'Ability to view payment schedules and their occurrence history',
			},
			UPDATE: {
				label: 'Update Payment Schedules',
				action: 'transactions:payment_schedules:update',
				description: 'Ability to edit, pause, and resume payment schedules',
			},
			DELETE: {
				label: 'Cancel Payment Schedules',
				action: 'transactions:payment_schedules:delete',
				description: 'Ability to cancel payment schedules',
			},
		},
	},
	USER: {
		label: 'User',
		READ: {
			label: 'View Users',
			action: 'user:read',
			description: 'Ability to view user details and lists',
		},
		CREATE: {
			label: 'Create Users',
			action: 'user:create',
			description: 'Ability to create new users',
		},
		UPDATE: {
			label: 'Edit Users',
			action: 'user:update',
			description: 'Ability to edit existing users',
		},
		UPDATE_SELF: {
			label: 'Edit Own Profile',
			action: 'user:update:self',
			description: 'Ability to edit your own user profile and information',
		},
		AVAILABILITY_SET: {
			label: 'Set User Availability',
			action: 'user:availability:set',
			description:
				"Ability to set another user's availability status (available/away/offline) as an admin override",
		},
		// PD-13861 — the blanket "Delegate As anyone" grant. This replaces the
		// old shortcut where the platform flags `dvp._userMap._admin` /
		// `._billing` implied delegate-as-anyone, so delegation access is now a
		// tenant RBAC decision like every other permission. Users without it can
		// still delegate as anyone who explicitly granted them access on their
		// own Profile > Delegation tab. Non-Workd holders can never target Workd
		// staff — that restriction is enforced in the login app and is not
		// configurable.
		DELEGATE_ANY: {
			label: 'Delegate As Any User',
			action: 'user:delegate:any',
			description:
				'Ability to delegate as any active user in the instance, without needing that user to grant access individually',
		},
		// PD-14923 — dedicated permission for the Lead Eligibility settings page.
		// This is the SOLE gate for managing lead eligibility (card visibility,
		// row/bulk edit, PUT endpoints); user:update intentionally does NOT
		// grant it, so the option can be granted/revoked independently of full
		// user management (SM1 parity: Sales Manager + Admin).
		LEAD_ELIGIBILITY_MANAGE: {
			label: 'Manage Lead Eligibility',
			action: 'user:lead_eligibility:manage',
			description:
				'Ability to view and edit lead distribution eligibility settings for sales reps',
		},
		COMPANIES: {
			label: 'Company Assignments',
			READ: {
				label: 'View User Companies',
				action: 'user:companies:read',
				description: 'Ability to view company assignments for users',
			},
			UPDATE: {
				label: 'Edit User Companies',
				action: 'user:companies:update',
				description: 'Ability to edit company assignments for any user',
			},
			UPDATE_SELF: {
				label: 'Edit Own Companies',
				action: 'user:companies:update:self',
				description: 'Ability to edit your own company assignments',
			},
		},
		WAREHOUSES: {
			label: 'Warehouse Assignments',
			READ: {
				label: 'View User Warehouses',
				action: 'user:warehouses:read',
				description: 'Ability to view warehouse assignments for users',
			},
			UPDATE: {
				label: 'Edit User Warehouses',
				action: 'user:warehouses:update',
				description: 'Ability to edit warehouse assignments for any user',
			},
			UPDATE_SELF: {
				label: 'Edit Own Warehouses',
				action: 'user:warehouses:update:self',
				description: 'Ability to edit your own warehouse assignments',
			},
		},
	},
	VENDORS: {
		label: 'Vendors',
		CREATE: {
			label: 'Create Vendors',
			action: 'vendors:create',
			description: 'Ability to create new vendors',
		},
		READ: {
			label: 'View Vendors',
			action: 'vendors:read',
			description: 'Ability to view vendor details and lists',
		},
		UPDATE: {
			label: 'Edit Vendors',
			action: 'vendors:update',
			description: 'Ability to edit existing vendors',
		},
		ADDRESSES: {
			label: 'Vendor Addresses',
			CREATE: {
				label: 'Create Vendor Addresses',
				action: 'vendors:addresses:create',
				description: 'Ability to create addresses associated with vendors',
			},
			READ: {
				label: 'View Vendor Addresses',
				action: 'vendors:addresses:read',
				description: 'Ability to view addresses associated with vendors',
			},
			UPDATE: {
				label: 'Edit Vendor Addresses',
				action: 'vendors:addresses:update',
				description: 'Ability to edit addresses associated with vendors',
			},
			DELETE: {
				label: 'Delete Vendor Addresses',
				action: 'vendors:addresses:delete',
				description: 'Ability to delete addresses associated with vendors',
			},
		},
		ITEMS: {
			label: 'Vendor Items',
			CREATE: {
				label: 'Create Vendor Items',
				action: 'vendors:items:create',
				description: 'Ability to create items associated with vendors',
			},
			READ: {
				label: 'View Vendor Items',
				action: 'vendors:items:read',
				description: 'Ability to view items associated with vendors',
			},
			UPDATE: {
				label: 'Edit Vendor Items',
				action: 'vendors:items:update',
				description: 'Ability to edit items associated with vendors',
			},
			DELETE: {
				label: 'Delete Vendor Items',
				action: 'vendors:items:delete',
				description: 'Ability to delete items associated with vendors',
			},
		},
	},
	/**
	 * Routes are the **blueprint** for a recurring delivery — a named
	 * collection of default stops (clients/addresses in a preferred
	 * sequence) plus default driver/vehicle/warehouse assignments and a
	 * recurrence rule. Routes are authored by warehouse management and
	 * consumed by dispatch to generate Trips.
	 *
	 * Pre-2026-05 these were called "Route Templates" (legacy DB table
	 * `route_template` still exists at the SQL layer); the entity was
	 * renamed to `RouteEntity` and the RBAC permissions follow suit.
	 * The previous nested `ROUTES.TEMPLATES.*` namespace was collapsed
	 * into the top-level `ROUTES.*` (migration: see
	 * `apps/config-api/src/migrations/instance/*RenameDistributionRbac*`).
	 */
	ROUTES: {
		label: 'Routes',
		CREATE: {
			label: 'Create Routes',
			action: 'routes:create',
			description:
				'Ability to create new routes (recurring delivery blueprints)',
		},
		READ: {
			label: 'View Routes',
			action: 'routes:read',
			description: 'Ability to view route details and lists',
		},
		UPDATE: {
			label: 'Edit Routes',
			action: 'routes:update',
			description: 'Ability to edit existing routes',
		},
		DELETE: {
			label: 'Delete Routes',
			action: 'routes:delete',
			description: 'Ability to delete routes',
		},
		VEHICLES: {
			label: 'Vehicles',
			CREATE: {
				label: 'Create Vehicles',
				action: 'routes:vehicles:create',
				description: 'Ability to create new vehicles',
			},
			READ: {
				label: 'View Vehicles',
				action: 'routes:vehicles:read',
				description: 'Ability to view vehicle details and lists',
			},
			UPDATE: {
				label: 'Edit Vehicles',
				action: 'routes:vehicles:update',
				description: 'Ability to edit existing vehicles',
			},
			DELETE: {
				label: 'Delete Vehicles',
				action: 'routes:vehicles:delete',
				description: 'Ability to delete vehicles',
			},
		},
		DRIVERS: {
			label: 'Drivers',
			READ: {
				label: 'View Drivers',
				action: 'routes:drivers:read',
				description: 'Ability to view driver details, metrics, and lists',
			},
			UPDATE: {
				label: 'Manage Drivers',
				action: 'routes:drivers:update',
				description: 'Ability to refresh driver metrics and manage driver data',
			},
		},
	},
	/**
	 * PD-13922 — merchandisers are the customer-placed freezer/cooler units
	 * (a real entity that owns its inventory bins).
	 *
	 * PD-15704 — relocated out of the ROUTES group to top level
	 * (`routes:merchandisers:*` → `merchandisers:*`): merchandisers are their
	 * own domain, and nesting them under routes forced warehouse roles to hold
	 * routes-namespace permissions to reach cooler screens. Granted rows are
	 * renamed by migration 1787714604473-RelocateMerchandiserRbacActions.
	 */
	MERCHANDISERS: {
		label: 'Merchandisers',
		CREATE: {
			label: 'Create Merchandisers',
			action: 'merchandisers:create',
			description: 'Ability to create new merchandisers',
		},
		READ: {
			label: 'View Merchandisers',
			action: 'merchandisers:read',
			description: 'Ability to view merchandiser details and lists',
		},
		UPDATE: {
			label: 'Edit Merchandisers',
			action: 'merchandisers:update',
			description: 'Ability to edit existing merchandisers',
		},
		DELETE: {
			label: 'Delete Merchandisers',
			action: 'merchandisers:delete',
			description: 'Ability to delete merchandisers',
		},
	},
	/**
	 * PD-15700 — sensor provisioning over Bluetooth. Linking a unit to the
	 * system (creating its records) and configuring the physical hardware
	 * (writing settings onto the unit) are deliberately two separate actions:
	 * creating records is safe and reversible, a hardware write is not.
	 * Both are seeded to Admin, Warehouse Manager, and Warehouse Associate.
	 */
	SENSORS: {
		label: 'Sensors',
		CREATE: {
			label: 'Add Sensors',
			action: 'sensors:create',
			description:
				'Ability to discover sensor units over Bluetooth and add them to the system',
		},
		CONFIGURE: {
			label: 'Configure Sensor Hardware',
			action: 'sensors:configure',
			description:
				'Ability to write settings (server URL, API key, APN) to physical sensor units',
		},
	},
	/**
	 * Trips are the **instances** — one day's run of a Route. A truck
	 * leaves the warehouse with a driver to deliver to a sequence of
	 * stops. The entity `TripEntity` replaces the legacy
	 * `route` + `route_execution` pair (those two tables collapsed into
	 * one; "execution_number" was dropped — a failed trip is a new
	 * trip, no retry semantics).
	 *
	 * Permission split mirrors who actually performs each action:
	 * - `CREATE` / `UPDATE` / `DELETE` — dispatch and warehouse mgmt.
	 *   The dispatch wizard creates trips; cancel/reassign/approve-
	 *   review are admin lifecycle ops.
	 * - `OPERATE` — drivers and warehouse personnel running the trip:
	 *   start, pause/resume, mark stop arrival, complete or skip
	 *   stops, capture signatures/photos/items, perform check-in and
	 *   check-out (pre-trip + post-trip vehicle inspections).
	 *
	 * Pre-rename: trip CRUD was the top-level `ROUTES.*` namespace and
	 * lifecycle ops were `ROUTES.EXECUTIONS.*`. Both folded into
	 * `TRIPS.*` here. See the migration noted on the ROUTES block above.
	 */
	TRIPS: {
		label: 'Trips',
		CREATE: {
			label: 'Create Trips (Dispatch)',
			action: 'trips:create',
			description:
				'Ability to ORIGINATE trips — the dispatch wizard (every api/dispatch endpoint, including commit) and direct trip creation. This is a dispatch/warehouse function; drivers do not originate trips, they start ones already dispatched to them (see `trips:start`). (PD-14883)',
		},
		/**
		 * PD-14883. Split out of `trips:create`.
		 *
		 * Migration 1779722092387 renamed the legacy `routes:executions:create`
		 * to `trips:create`. "Execution" meant a trip RUN, so the driver's
		 * "begin my run" permission was merged into "originate a trip" by a
		 * rename, and Driver has held `trips:create` ever since. That is why
		 * the Dispatch nav entry — correctly gated on `trips:create` — has been
		 * visible to drivers.
		 *
		 * `start-from-route` is what the driver's pre-trip inspection and trip
		 * execution pages call to begin a trip, so it needs its own action
		 * rather than borrowing the dispatch one.
		 *
		 * Named as a sibling token, not `trips:create:start`, so the implicit
		 * permission hierarchy cannot hand it to every `trips:create` holder
		 * (see the note on TRIPS.READ_ALL).
		 */
		START: {
			label: 'Start Trips',
			action: 'trips:start',
			description:
				"Ability to start a trip that has already been dispatched — the driver's begin-trip action from the pre-trip inspection and trip execution pages. Distinct from `trips:create`, which originates new trips via dispatch. (PD-14883)",
		},
		READ: {
			label: 'View Trips',
			action: 'trips:read',
			description:
				"Ability to view trip details, lists, and progress for the user's OWN trips (the driver's My Trips list and the trips they operate). Fleet-wide visibility is a separate permission — see `trips:read:all`.",
		},
		/**
		 * PD-14869. The action is deliberately named `trips:read_all` and NOT
		 * `trips:read:all`. `PermissionService.hasPathMatch` implements an
		 * implicit hierarchy in which a SHORTER permission grants every longer
		 * one beneath it (`clients:view` grants `clients:view:assigned`), with
		 * `:self` as the only carve-out. `trips:read:all` would therefore be
		 * granted automatically to every role already holding `trips:read` —
		 * including Driver — making the whole distinction a no-op. As a
		 * sibling token it cannot be captured by the hierarchy.
		 *
		 * `price_contracts:read:all` is safe only because no bare
		 * `price_contracts:read` action exists; that is not the case here.
		 */
		READ_ALL: {
			label: 'View All Trips (Fleet-Wide)',
			action: 'trips:read_all',
			description:
				"Ability to view every trip in the fleet regardless of assigned driver — the All Trips list and the dispatcher Live Board, including other drivers' vehicles and schedules. Drivers hold `trips:read` for their own work but NOT this. (PD-14869)",
		},
		UPDATE: {
			label: 'Edit Trips',
			action: 'trips:update',
			description:
				'Ability to edit trip metadata and perform dispatch/warehouse-management lifecycle actions: cancel, reassign driver, approve pre-trip and post-trip warehouse reviews, mid-trip inventory adjustments',
		},
		DELETE: {
			label: 'Delete Trips',
			action: 'trips:delete',
			description: 'Ability to delete trips',
		},
		OPERATE: {
			label: 'Operate Trips',
			action: 'trips:operate',
			description:
				'Ability to operate a trip during its run: start the trip, pause/resume, mark stop arrival, complete or skip stops, capture signatures/photos/items, perform check-in (pre-trip) and check-out (post-trip) vehicle inspections. Granted to drivers and warehouse personnel — distinct from `trips:update` which gates admin-only lifecycle actions.',
		},
		PHOTOS_UPLOAD_FROM_LIBRARY: {
			label: 'Upload Trip Photos from Library',
			action: 'trips:photos:upload_from_library',
			description:
				'Ability to add trip/stop photos from the device photo library instead of only the live camera. Drivers do NOT get this — they are camera-only so they cannot re-use old gallery images for proof-of-delivery/inspection. Granted to Warehouse Manager and Admin. (PD-13766)',
		},
		STOP_CREDIT_MEMO: {
			label: 'Issue Credit Memos at a Stop',
			action: 'trips:stop:credit_memo',
			description:
				'Ability to record a credit for spoiled/damaged/short product while completing a delivery stop, producing a credit memo through the stop-completion flow only. Deliberately narrower than invoices:credit_memo:create — it grants no access to the office credit-memo screens, and the credit stays bounded by the stop context (fixed reason list, required photo, recent-billing limits). Granted to the Driver role. (PD-15073)',
		},
	},
	WAREHOUSES: {
		label: 'Warehouses',
		CREATE: {
			label: 'Create Warehouses',
			action: 'warehouses:create',
			description: 'Ability to create new warehouses',
		},
		READ: {
			label: 'View Warehouses',
			action: 'warehouses:read',
			description: 'Ability to view warehouse details and lists',
		},
		UPDATE: {
			label: 'Edit Warehouses',
			action: 'warehouses:update',
			description: 'Ability to edit existing warehouses',
		},
	},
	ECOMMERCE_USERS: {
		label: 'Ecommerce Users',
		CREATE: {
			label: 'Create Ecommerce Users',
			action: 'ecommerce_users:create',
			description: 'Ability to create new ecommerce users',
		},
		READ: {
			label: 'View Ecommerce Users',
			action: 'ecommerce_users:read',
			description: 'Ability to view ecommerce user details and lists',
		},
		UPDATE: {
			label: 'Edit Ecommerce Users',
			action: 'ecommerce_users:update',
			description: 'Ability to edit existing ecommerce users',
		},
		DELETE: {
			label: 'Delete Ecommerce Users',
			action: 'ecommerce_users:delete',
			description: 'Ability to delete ecommerce users',
		},
	},
	PORTAL_SITES: {
		label: 'Portal Sites',
		READ: {
			label: 'View Portal Sites',
			action: 'portal_sites:read',
			description:
				'Ability to view portal site (brand) configurations for the tenant',
		},
		MANAGE: {
			label: 'Manage Portal Sites',
			action: 'portal_sites:manage',
			description:
				'Ability to create, edit, and deactivate portal sites (brands), including scope (companies/warehouses) and branding',
		},
		MANAGE_DOMAINS: {
			label: 'Manage Portal Site Domains',
			action: 'portal_sites:manage_domains',
			description:
				'Ability to add, verify, and assign custom domains to portal sites',
		},
	},
	SETTINGS: {
		label: 'Settings',
		BUSINESS_DAYS: {
			label: 'Business Days',
			READ: {
				label: 'View Business Days',
				action: 'settings:business_days:read',
				description:
					'Ability to view the business-day configuration (working week and holidays/closures)',
			},
			MANAGE: {
				label: 'Manage Business Days',
				action: 'settings:business_days:manage',
				description:
					'Ability to change the working week and add/remove holidays and closures that affect sales-goal proration',
			},
		},
		PDF_TEMPLATES: {
			label: 'PDF Templates',
			READ: {
				label: 'View PDF Templates',
				action: 'settings:pdf_templates:read',
				description:
					'Ability to view the PDF template list and open templates in the builder read-only',
			},
			MANAGE: {
				label: 'Manage PDF Templates',
				action: 'settings:pdf_templates:manage',
				description:
					'Ability to create, edit, assign, and delete the PDF templates used to render invoice documents',
			},
		},
		API_KEYS: {
			label: 'Tenant API Keys',
			CREATE: {
				label: 'Create API Keys',
				action: 'settings:api_keys:create',
				description: 'Ability to create new tenant API keys',
			},
			READ: {
				label: 'View API Keys',
				action: 'settings:api_keys:read',
				description: 'Ability to view tenant API key details and lists',
			},
			UPDATE: {
				label: 'Edit API Keys',
				action: 'settings:api_keys:update',
				description:
					'Ability to edit tenant API key settings and RBAC permissions',
			},
			DELETE: {
				label: 'Delete API Keys',
				action: 'settings:api_keys:delete',
				description: 'Ability to revoke or permanently delete tenant API keys',
			},
		},
		SENSOR_FIRMWARE: {
			label: 'Sensor Firmware',
			CREATE: {
				label: 'Create Sensor Firmware',
				action: 'settings:sensor_firmware:create',
				description:
					'Ability to upload new sensor firmware versions for OTA updates',
			},
			READ: {
				label: 'View Sensor Firmware',
				action: 'settings:sensor_firmware:read',
				description: 'Ability to view sensor firmware versions and history',
			},
			UPDATE: {
				label: 'Edit Sensor Firmware',
				action: 'settings:sensor_firmware:update',
				description:
					'Ability to edit sensor firmware version details (effective date, update flag)',
			},
			DELETE: {
				label: 'Delete Sensor Firmware',
				action: 'settings:sensor_firmware:delete',
				description: 'Ability to delete sensor firmware versions',
			},
		},
		PAYMENT_CONFIGURATION: {
			label: 'Payment Configuration',
			CREATE: {
				label: 'Create Payment Configuration',
				action: 'settings:payment_configuration:create',
				description:
					'Ability to create payment configuration (payment method types and API configurations)',
			},
			READ: {
				label: 'View Payment Configuration',
				action: 'settings:payment_configuration:read',
				description:
					'Ability to view payment configuration (payment method types and API configurations)',
			},
			UPDATE: {
				label: 'Edit Payment Configuration',
				action: 'settings:payment_configuration:update',
				description:
					'Ability to edit payment configuration (payment method types and API configurations)',
			},
			DELETE: {
				label: 'Delete Payment Configuration',
				action: 'settings:payment_configuration:delete',
				description:
					'Ability to delete payment configuration (payment method types and API configurations)',
			},
		},
		VIEW_LAYOUTS: {
			label: 'View Layouts',
			MANAGE: {
				label: 'Manage View Layouts',
				action: 'settings:view_layouts:manage',
				description:
					'Ability to create, edit, and delete view layout configurations for entity detail pages',
			},
		},
		TAB_CONFIGURATIONS: {
			label: 'Tab Configurations',
			MANAGE: {
				label: 'Manage Tab Configurations',
				action: 'settings:tab_configurations:manage',
				description:
					'Ability to create, edit, and delete default tab configurations for list views',
			},
		},
		THEME: {
			label: 'Theme',
			MANAGE: {
				label: 'Manage Theme Settings',
				action: 'settings:theme:manage',
				description:
					'Ability to update theme branding settings such as logo URL',
			},
		},
		SHIPPING_METHODS: {
			label: 'Shipping Methods',
			MANAGE: {
				label: 'Manage Shipping Methods',
				action: 'settings:shipping_methods:manage',
				description:
					'Ability to create, edit, and delete shipping method configurations',
			},
		},
		TERMS_CODES: {
			label: 'Terms Codes',
			MANAGE: {
				label: 'Manage Terms Codes',
				action: 'settings:terms_codes:manage',
				description:
					'Ability to create, edit, and delete payment-terms catalog entries (Net 30, EOM+10, early-pay discounts, etc.)',
			},
		},
		CATALOG_VALUES: {
			label: 'Catalog Values',
			MANAGE: {
				label: 'Manage Catalog Values',
				action: 'settings:catalog_values:manage',
				description:
					'Ability to manage consolidated lookup catalogs (merchandiser manufacturers and models, and other managed value lists).',
			},
		},
		CLIENT_DEFAULTS: {
			label: 'Client Defaults',
			MANAGE: {
				label: 'Manage Client Defaults',
				action: 'settings:client_defaults:manage',
				description:
					'Ability to set per-tenant defaults applied to new clients (e.g. the default State pre-filled on the New Client form).',
			},
		},
		ORDER_SUBMISSION_CHECKS: {
			label: 'Order Submission Checks',
			MANAGE: {
				label: 'Manage Order Submission Checks',
				action: 'settings:order_submission_checks:manage',
				description: 'Ability to manage order submission check configurations',
			},
		},
		PURCHASE_ORDER_APPROVAL_CHECKS: {
			label: 'PO Approval Checks',
			MANAGE: {
				label: 'Manage PO Approval Checks',
				action: 'settings:po_approval_checks:manage',
				description:
					'Ability to manage the PO approval threshold and other purchasing-approval rules',
			},
		},
		DISTRIBUTION_CONFIG: {
			label: 'Distribution Settings',
			MANAGE: {
				label: 'Manage Distribution Settings',
				action: 'settings:distribution_config:manage',
				description:
					'Ability to configure the Dispatch wizard fill-% thresholds (refill below, emergency) and orphan-order window',
			},
		},
		AUTHENTICATION: {
			label: 'Authentication',
			MANAGE: {
				label: 'Manage Authentication Settings',
				action: 'settings:authentication:manage',
				description:
					'Ability to enable or disable authentication methods such as phone login for CRM and portal users',
			},
		},
		DOCUMENT_COPIES: {
			label: 'Document Copies',
			MANAGE: {
				label: 'Manage Document Copies',
				action: 'settings:document_copies:manage',
				description:
					'Ability to configure who at the company is copied (CC or BCC) on customer-facing documents such as invoices and delivery receipts',
			},
		},
		ISSUE_NOTIFICATIONS: {
			label: 'Issue Notifications',
			MANAGE: {
				label: 'Manage Issue Notifications',
				action: 'settings:issue_notifications:manage',
				description:
					"Ability to configure which RBAC roles are looped in (then reached in-app, by email, or by push per each user's own notification preferences) when an issue of a given category (Vehicle, Client, General, ...) is created or updated",
			},
		},
	},
	SOURCING_REQUESTS: {
		label: 'Sourcing Requests',
		READ: {
			label: 'View Sourcing Requests',
			action: 'sourcing_requests:read',
			description: 'Ability to view sourcing request details and lists',
		},
		CREATE: {
			label: 'Create Sourcing Requests',
			action: 'sourcing_requests:create',
			description: 'Ability to create new sourcing requests',
		},
		UPDATE: {
			label: 'Edit Sourcing Requests',
			action: 'sourcing_requests:update',
			description: 'Ability to edit existing sourcing requests',
		},
		DELETE: {
			label: 'Delete Sourcing Requests',
			action: 'sourcing_requests:delete',
			description: 'Ability to delete sourcing requests',
		},
		VIEW_SELF: {
			label: 'View Assigned Sourcing Requests',
			action: 'sourcing_requests:view:self',
			description:
				'Can view sourcing requests assigned to yourself or linked to your clients',
		},
		VIEW_ANY: {
			label: 'View All Sourcing Requests',
			action: 'sourcing_requests:view:any',
			description: 'Can view all sourcing requests regardless of assignment',
		},
		LINES: {
			label: 'Sourcing Request Lines',
			CREATE: {
				label: 'Add Lines',
				action: 'sourcing_requests:lines:create',
				description: 'Ability to add line items to sourcing requests',
			},
			UPDATE: {
				label: 'Edit Lines',
				action: 'sourcing_requests:lines:update',
				description: 'Ability to edit line items in sourcing requests',
			},
			DELETE: {
				label: 'Delete Lines',
				action: 'sourcing_requests:lines:delete',
				description: 'Ability to delete line items from sourcing requests',
			},
		},
		ASSIGN_VENDOR: {
			label: 'Assign Vendors',
			action: 'sourcing_requests:assign_vendor',
			description: 'Ability to assign vendors to sourcing request lines',
		},
		MATCH_ITEM: {
			label: 'Match Items',
			action: 'sourcing_requests:match_item',
			description: 'Ability to match lines to catalog items',
		},
		CREATE_PO: {
			label: 'Create Purchase Orders',
			action: 'sourcing_requests:create_po',
			description:
				'Ability to create purchase orders from sourcing request lines',
		},
		SEND_RFQ: {
			label: 'Send RFQs',
			action: 'sourcing_requests:send_rfq',
			description:
				'Ability to send a Request-for-Quote to one or more vendors for a sourcing request',
		},
		AWARD_RFQ: {
			label: 'Award RFQs',
			action: 'sourcing_requests:award_rfq',
			description:
				'Ability to accept a vendor quote and convert it into a committed PO (whole-PO or per-line)',
		},
		BULK_ASSIGN: {
			label: 'Bulk Assign User',
			action: 'sourcing_requests:bulk_assign',
			description:
				'Ability to bulk-assign sourcing requests to a user from the list view',
		},
		NOTES: {
			label: 'Sourcing Request Notes',
			CREATE: {
				label: 'Create Notes',
				action: 'sourcing_requests:notes:create',
				description: 'Ability to add notes to sourcing requests',
			},
			UPDATE_SELF: {
				label: 'Edit Own Notes',
				action: 'sourcing_requests:notes:update:self',
				description: 'Ability to edit notes you created',
			},
			UPDATE_ANY: {
				label: 'Edit Any Notes',
				action: 'sourcing_requests:notes:update:any',
				description: 'Ability to edit any notes',
			},
			DELETE_SELF: {
				label: 'Delete Own Notes',
				action: 'sourcing_requests:notes:delete:self',
				description: 'Ability to delete notes you created',
			},
		},
	},
	CUSTOM_FIELDS: {
		label: 'Custom Fields',
		VIEW_EDIT: {
			label: 'View & Edit Custom Field Definitions',
			action: 'custom_fields:view_edit',
			description:
				'Ability to view and manage custom field definitions in settings',
		},
		VALUES: {
			label: 'Custom Field Values',
			EDIT: {
				label: 'Edit Custom Field Values',
				action: 'custom_fields:values:edit',
				description: 'Ability to edit custom field values on entities',
			},
		},
	},
	REGISTRATION_FIELDS: {
		label: 'Registration Form Fields',
		VIEW_EDIT: {
			label: 'View & Edit Registration Form Fields',
			action: 'registration_fields:view_edit',
			description:
				'Ability to view and manage storefront registration form field definitions in settings',
		},
	},
} as const;

/**
 * Helper function to flatten the nested structure to get all actions
 * Useful for processing or searching all actions regardless of their grouping
 */
export function getAllRbacActions(): RbacActionDefinition[] {
	const actions: RbacActionDefinition[] = [];

	function traverse(item: unknown): void {
		if (isRbacAction(item)) {
			actions.push(item);
			return;
		}

		if (typeof item === 'object' && item !== null) {
			for (const key in item) {
				// Skip the label property of groups
				if (key === 'label' && !isRbacAction(item)) {
					continue;
				}

				const value = item[key as keyof typeof item] as unknown;
				if (typeof value === 'object' && value !== null) {
					traverse(value);
				}
			}
		}
	}

	traverse(RbacActions);
	return actions;
}

/**
 * Helper function to find an action by its action identifier
 */
export function findRbacActionByAction(
	action: string,
): RbacActionDefinition | undefined {
	const allActions = getAllRbacActions();
	return allActions.find((a) => a.action === action);
}

/**
 * Helper function to get the grouped structure for UI display
 * Returns the structure with groups and actions organized hierarchically.
 *
 * `dynamicActions` lets callers fold in actions that aren't part of the static
 * RbacActions tree — currently the data-driven custom-report actions surfaced by
 * RbacService.getActions (`reporting:custom:view:<reportKey>`). Any provided
 * action whose name isn't already in the static tree is grouped under a synthetic
 * "Custom Reports" group so it renders, groups, searches and assigns like every
 * other action in the RBAC management UI. Without this the assignment components
 * silently drop unknown actions (they only traverse the static tree).
 */
export function getRbacActionsGrouped(
	dynamicActions: Array<{ _name: string; _label?: string }> = [],
): RbacActionsModel {
	if (dynamicActions.length === 0) {
		return RbacActions;
	}

	const knownActionNames = new Set(
		getAllRbacActions().map((action) => action.action),
	);
	const extras = dynamicActions.filter(
		(action) => !knownActionNames.has(action._name),
	);
	if (extras.length === 0) {
		return RbacActions;
	}

	const customReportsGroup: RbacActionGroup = { label: 'Custom Reports' };
	for (const action of extras) {
		customReportsGroup[action._name] = {
			action: action._name,
			label: action._label ?? action._name,
			description: action._label ?? action._name,
		};
	}

	return { ...RbacActions, customReports: customReportsGroup };
}
